import { sc, UI_SCALE } from "@/utils/uiScale";
import { DENSITY_PRESETS, useGridDensity } from "@/utils/gridDensity";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RosterCanvas } from "@/canvas/RosterCanvas";
import { DragBar } from "@/components/DragBar";
import { useDragOffset } from "@/utils/useDragOffset";
import { GridHoverCard } from "@/components/GridHoverCard";
import { CounterAssignPopover } from "@/components/CounterAssignPopover";
import { DUTY_FILL, NON_COUNTER_LEGEND_LABEL, choiceOf } from "@/utils/counterChoice";
import { apiClient } from "@/api/client";
import { useNotification } from "@/contexts/NotificationContext";
import { useLiveNowSlot } from "@/hooks/useLiveNowSlot";
import { LiveNowFlag } from "@/components/LiveNowLine";
import { useOtColorVersion } from "@/utils/otColors";
import { formatCounterLabel, getStartHour, hmmmToSlot, hmmmToEndSlot, naturalSortOfficers, getOfficerColor } from "@/config/modeConfig";
import { useOfficerOrder, saveOfficerOrder } from "@/utils/officerOrder";
import { timingSpan } from "@/utils/partialTiming";
import { useDialogScale } from "@/hooks/useDialogScale";
import { useSession } from "@/contexts/SessionContext";
import { CallPhoneButton } from "@/components/CallPhoneButton";
import { NoReliefCellOverlay } from "@/components/NoReliefCellOverlay";
import { useNoReliefView } from "@/contexts/NoReliefContext";
import type { CellAction } from "@/utils/cellAction";
import { CallPhoneOverlay } from "@/components/CallPhoneOverlay";
import { callIcons, nrRightInsets } from "@/utils/callIcons";
import { ackKey, type CallBlock } from "@/utils/callBlocks";
import { mergeAvailabilitySegments, officerWindows, slotInWindows, windowsText, type AvailWindow } from "@/utils/availabilityWindows";
import { ReadingRulerBands, RulerHeaderMarker, RulerToggle } from '@/components/ReadingRuler';
import { loadPref, savePref } from "@/utils/prefStorage";
import { counterVacancyOnSchedules } from "@/utils/counterVacancyFromGrid";
import { useReadingRuler, rulerColor } from '@/utils/readingRuler';

import type {
  RosterGridData,
  AvailabilityBracket,
  OfficerMetadata,
  HoverInfo,
  ManualAllocation,
  OperationResult,
} from "@/types/api";

const LABEL_LINE_H = 1.3;
const NAME_MAX_LINES = 3;
const LABEL_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

/**
 * Every size that depends on the officer grid's text-size preset (utils/gridDensity):
 * the row label's fonts and column, the row heights they imply, the time header. One
 * `scale` moves them together, so a row label never outgrows its row or its column.
 */
/** Width of the call-badge column, and of the drag handle + eye shown instead of it in Edit mode (px, before UI scale). */
const CALL_COL_PX = 26;
const GRIP_COL_PX = 16;
const EYE_COL_PX = 26;
/** How far a row must be pulled left, as a share of its width, for release to ask to delete it. */
const SWIPE_ARM_FRACTION = 0.5;
/** The furthest a row follows the finger, as a share of its width. */
const SWIPE_MAX_FRACTION = 0.75;
const SWIPE_HINT_KEY = 'ac_roster_swipe_hint_seen';

function officerMetrics(scale: number, callSlot: boolean, editing: boolean) {
  const namePx = 8 * UI_SCALE * scale;
  const timePx = 7 * UI_SCALE * scale;
  return {
    // The name keeps the same width in both modes (so row heights never change when Edit is
    // toggled); the column grows by whatever sits beside it: the call badge normally, the drag
    // handle and eye in Edit mode.
    callW: callSlot && !editing ? sc(CALL_COL_PX) : 0,
    gripW: editing ? sc(GRIP_COL_PX) : 0,
    eyeW: editing ? sc(EYE_COL_PX) : 0,
    labelW: Math.round(sc(80) * scale) + (editing ? sc(GRIP_COL_PX) + sc(EYE_COL_PX) : callSlot ? sc(CALL_COL_PX) : 0),
    headerH: Math.round(sc(25) * scale),
    headerFont: Math.round(sc(11) * scale),
    namePx,
    timePx,
    // Usable text width inside a label: the name's own share of the column, less its padding.
    textW: Math.round(sc(72) * scale),
    nameFont: `700 ${namePx}px ${LABEL_FONT_FAMILY}`,
    timeFont: `600 ${timePx}px ${LABEL_FONT_FAMILY}`,
    // Minimum row = 2 name lines + 1 time line + 6 px padding.
    minH: Math.ceil((2 * namePx + timePx) * LABEL_LINE_H) + sc(6),
    // Maximum row = 4 lines total (3 name + 1 time) + padding — caps resizeToFit expansion.
    maxH: Math.ceil((NAME_MAX_LINES * namePx + timePx) * LABEL_LINE_H) + 6,
  };
}
type OfficerMetrics = ReturnType<typeof officerMetrics>;

/** Shared offscreen 2D context for text measurement (created once, lazily). */
let measureCtx: CanvasRenderingContext2D | null | undefined;
function textWidth(text: string, font: string): number {
  if (measureCtx === undefined) {
    measureCtx = document.createElement('canvas').getContext('2d');
  }
  if (!measureCtx) return text.length * 8 * UI_SCALE * 0.55;  // no canvas (SSR/tests)
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}

/**
 * How many lines `text` wraps into within `maxWidth`, measured with the real font
 * rather than an assumed average character width.
 *
 * The row height this feeds is what the canvas paints its rows at AND what the
 * label box is fixed to, so an underestimate here is what makes a row header
 * spill past its own row. Mirrors the label's `word-break: break-word`: words
 * wrap whole, and a word too long for a line is split across lines.
 */
function wrappedLineCount(text: string, maxWidth: number, font: string): number {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 1;
  let lines = 1;
  let lineW = 0;
  for (const word of words) {
    let w = textWidth(word, font);
    const spaceW = lineW > 0 ? textWidth(' ', font) : 0;
    if (lineW > 0 && lineW + spaceW + w > maxWidth) { lines++; lineW = 0; }
    // A single word wider than the line breaks across as many lines as it needs.
    while (w > maxWidth) {
      lines++;
      w -= maxWidth;
    }
    lineW += (lineW > 0 ? spaceW : 0) + w;
  }
  return lines;
}

/** Estimate pixel height for one officer row based on label content. */
function estimateOfficerRowH(m: OfficerMetrics, nameText: string, hasTime: boolean, extraTimeLines = 0): number {
  const lines = wrappedLineCount(nameText, m.textW, m.nameFont);
  const nameLines = Math.min(lines, NAME_MAX_LINES);
  const timeLines = (hasTime ? 1 : 0) + extraTimeLines;
  const px = Math.ceil((nameLines * m.namePx + timeLines * m.timePx) * LABEL_LINE_H) + 6;
  // Anything past this scrolls inside the label rather than growing the row further.
  return Math.max(20, Math.min(m.maxH + 2 * Math.ceil(m.timePx * LABEL_LINE_H), px));
}

// Availability window cell color — visually distinct, similar vibrancy to other officer colors
const AVAIL_UNASSIGNED_COLOR = "#6ee7b7";

// Zero runs shorter than this between the first and last counter assignment are
// treated as scheduled breaks (shown as "#") for SOS/OT officers.
const SHORT_BREAK_SLOTS = 12;

// Full-shift bounds per shift type — used to detect partial-shift main officers
const SHIFT_BOUNDS: Record<string, { start: string; end: string }> = {
  morning: { start: "1000", end: "2200" },
  night:   { start: "2200", end: "1000" },
};

interface OfficerScheduleGridProps {
  officer_schedules: Record<string, number[]>;
  officer_metadata: Record<string, OfficerMetadata>;
  time_labels: string[];
  num_slots: number;
  operation_mode?: string;
  shiftType?: string;
  numCounters?: number;
  height?: string;
  /**
   * When set, the container grows to exactly fit every officer row instead of
   * clipping/scrolling them internally in a fixed-height box — the page
   * scrolls for the rest instead, matching RosterGrid's growToFitRows. `height`
   * is ignored in this mode (there is no fixed budget to fill or cap). Also
   * locks out the canvas pane's own vertical scroll (horizontal/time scroll
   * is unaffected), since a bounded inner scroll region and a scrollable page
   * fighting over the same vertical swipe is confusing. Off (default) keeps
   * the old fixed-height, internally-scrollable pane split view relies on.
   */
  growToFitRows?: boolean;
  onUpdated?: (result?: Pick<OperationResult, 'snapshot'>) => void;
  /** Clear a set of [startSlot, endSlot) ranges off the shared grid state RIGHT NOW,
   * before the deleteOfficer round trip resolves — erasing a counter assignment
   * used to sit there, visibly unchanged, until the whole delete/statistics
   * round trip came back. Returns a rollback to call if the request fails. */
  onOptimisticClear?: (ranges: Array<{ officerKey: string; startSlot: number; endSlot: number }>) => () => void;
  /** Strips officers off the shared grid state RIGHT NOW, before the remove round
   * trip resolves. Returns a rollback to call if the request fails. */
  onOptimisticRemove?: (officerKeys: string[]) => () => void;
  closeSignal?: number;
  onInteraction?: () => void;
  onOfficerClick?: (info: HoverInfo) => void;
  hiddenOfficerIds?: Set<string>;
  onChangeHiddenIds?: (ids: Set<string>) => void;
  highlightOfficerIds?: Set<string>;
  /** Live availability override from the open edit popup — enables instant bracket preview on canvas. */
  availabilityOverride?: { officerId: string; segs: Array<{ start: string; end: string }> } | null;
  /**
   * Called the moment a grid-driven availability edit (dropdown change, split,
   * add, delete) computes its next value — lets the parent feed it back in as
   * availabilityOverride so the canvas updates instantly instead of waiting on
   * the network round-trip that follows.
   */
  onAvailabilityPreview?: (officerId: string, segs: Array<{ start: string; end: string }>) => void;
  /**
   * Officer IDs currently shown in the "availability layer" — the counter/break
   * layer is hidden for these rows, showing only green (available) vs default
   * background plus inline start/end/delete controls for each availability window.
   * Toggled in bulk, for every officer at once, by the global "<>" layer button —
   * there is no per-row entry/exit gesture on the grid itself.
   */
  showAvailabilityRows?: Set<string>;
  /** Per officer: how many blocks need a phone call and how many are still owed. An
   *  officer with at least one gets a phone icon on their row label. */
  callStates?: Map<string, { pending: unknown[]; total: number }>;
  /** The same call blocks and acknowledged set the counter grid draws its phone icons from — one source of truth, so ticking either tab ticks both. */
  callBlocks?: CallBlock[];
  callAcked?: Set<string>;
  onToggleCall?: (block: CallBlock, called: boolean) => void;
  /** Mark all of an officer's blocks phoned about (true) or re-arm them all (false). */
  onToggleOfficerCall?: (officerId: string, called: boolean) => void;
  /** The initially-generated roster's officer_schedules (history[0]) — a main
   *  officer's slot whose counter here doesn't match this baseline is drawn in
   *  red (see CellData.changed). Omit to skip the comparison entirely. */
  initialOfficerSchedules?: Record<string, number[]>;
  /** Per-assignment-segment captions: officer_key -> {slot_index (as string) ->
   *  note text}. Rendered on the canvas via CellData.note. See RosterState.assignment_notes. */
  assignmentNotes?: Record<string, Record<string, string>>;
  /** Free-text duties: officer_key -> {slot_index (as string) -> text}. Working time off the
   *  counters, drawn hatched with its text (CellData.duty) and never read as a break. See
   *  RosterState.duty_slots. */
  dutySlots?: Record<string, Record<string, string>>;
  /** The slots the roster PDF prints as a break ("#"), per officer key — drawn as breaks exactly as printed
   *  (see utils/printedBreaks), whatever the grid would otherwise infer from the counters around them. */
  printedBreaks?: Record<string, number[]>;
  /** End-of-shift HOTO notes, officer_key -> text. Passing `onEndNoteChange` adds a
   *  column to the right of the last time slot with an input on each OT officer's row —
   *  for an OT officer who is on break till the shift ends and reports to the next
   *  shift's car field. Read by the HOTO message (utils/hotoMessage.ts). */
  endNotes?: Record<string, string>;
  onEndNoteChange?: (officerKey: string, text: string) => void;
}


/** Width of the HOTO note column that follows the last time slot. */
const END_NOTE_W = 190;

function officerLabel(officerId: string, meta?: OfficerMetadata): string {
  if (!meta) return officerId;
  const parts = [meta.deployment, meta.name].filter(Boolean).join(" - ");
  if (!parts) return officerId;
  return parts;
}

function slotToHmmm(slot: number, startHour: number, slotMinutes: number): string {
  const total = startHour * 60 + slot * slotMinutes;
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${h.toString().padStart(2, "0")}${m.toString().padStart(2, "0")}`;
}

function calcDurationMins(start: string, end: string, shiftType: string): number {
  const sh = getStartHour(shiftType);
  return (hmmmToEndSlot(end, sh) - hmmmToSlot(start, sh)) * 15;
}

function fmtTime(t: string): string {
  return `${t.slice(0, 2)}:${t.slice(2)}`;
}

/**
 * What a main officer's y-label says about their time. Excluded ("official duty") ranges are
 * taken out of the availability window, so the label lists when they can actually work, and a
 * separate duty line lists when they are away — which is not a break, and shows as a gap, not "#".
 */
function mainExclusionText(meta: OfficerMetadata | undefined, startHour: number, availStart: string | null, availEnd: string | null, fallbackStart: string | null, fallbackEnd: string | null): { windows: string; duty: string } | null {
  const excl = meta?.excluded_times;
  if (!excl || excl.length === 0) return null;
  const start = availStart || fallbackStart;
  const end = availEnd || fallbackEnd;
  if (!start || !end) return null;
  const mask = Array<boolean>(48).fill(false);
  for (let i = hmmmToSlot(start, startHour); i < hmmmToEndSlot(end, startHour); i++) mask[i] = true;
  for (const r of excl) {
    for (let i = hmmmToSlot(r.start, startHour); i < hmmmToEndSlot(r.end, startHour); i++) mask[i] = false;
  }
  const runs: string[] = [];
  for (let i = 0; i < 48; i++) {
    if (!mask[i]) continue;
    let j = i;
    while (j < 48 && mask[j]) j++;
    runs.push(`${fmtTime(slotToHmmm(i, startHour, 15))}–${fmtTime(slotToHmmm(j, startHour, 15))}`);
    i = j;
  }
  return {
    windows: runs.join(' · '),
    duty: `Duty ${excl.map((r) => `${r.start}–${r.end}`).join(' · ')}`,
  };
}

export function OfficerScheduleGrid({
  officer_schedules,
  officer_metadata,
  time_labels,
  num_slots,
  operation_mode,
  shiftType = "morning",
  numCounters = 41,
  height,
  growToFitRows = false,
  onUpdated,
  onOptimisticClear,
  onOptimisticRemove,
  closeSignal,
  onInteraction,
  onOfficerClick,
  hiddenOfficerIds,
  onChangeHiddenIds,
  highlightOfficerIds,
  availabilityOverride,
  onAvailabilityPreview,
  showAvailabilityRows,
  callStates,
  callBlocks,
  callAcked,
  onToggleCall,
  onToggleOfficerCall,
  initialOfficerSchedules,
  assignmentNotes,
  dutySlots,
  printedBreaks,
  endNotes,
  onEndNoteChange,
}: OfficerScheduleGridProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<RosterCanvas | null>(null);
  const liveNow = useLiveNowSlot(shiftType);
  const { canEdit } = useSession();
  const { ruler, tapHandlers } = useReadingRuler();

  // ── User-chosen row order (drag a row's grip handle to move it) ───────────
  // Lives in utils/officerOrder.ts — the one order every list and dropdown reads.
  const customOrder = useOfficerOrder();
  const saveCustomOrder = (next: string[]) => {
    void saveOfficerOrder(next).then(ok => { if (!ok) notifyError('Could not save the officer order'); });
  };
  const orderOfficers = (ids: string[]): string[] => naturalSortOfficers(ids);
  // One scroll container for the whole grid; the time header and the label column are
  // position:sticky inside it, so they scroll with the canvas natively (see RosterGrid).
  const gridPanelRef = useRef<HTMLDivElement>(null);
  const mainScrollRef = gridPanelRef;


  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  // The pointer, kept out of React state so a mousemove does not re-render the grid
  // (see RosterGrid); the tooltip follows it by direct style writes.
  const mousePosRef = useRef<{ x: number; y: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [dragSelection, setDragSelection] = useState<{
    row: number;
    officerId: string;
    startTime: string;
    endTime: string;
    x: number;
    y: number;
    mode: 'add' | 'erase';
  } | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  // Close any open drag popover in this grid when the parent signals exclusivity
  useEffect(() => {
    setDragSelection(null);
  }, [closeSignal]);

  // Text-size preset for this grid (per device) and every size derived from it.
  const [density] = useGridDensity('officer');
  // Reserve the call slot in every row once any officer has a call to make or tick, so the
  // button sits in the same place on every row and never shifts the other buttons.
  const [editMode, setEditMode] = useState(false);
  const hasCalls = useMemo(() => !!callStates && [...callStates.values()].some((c) => c.total > 0), [callStates]);
  const dm = useMemo(() => officerMetrics(DENSITY_PRESETS[density].fontCap, hasCalls, editMode), [density, hasCalls, editMode]);

  const [layoutInfo, setLayoutInfo] = useState<{
    labelWidth: number; labelHeight: number; cellWidth: number; cellHeight: number;
  }>({ labelWidth: 0, labelHeight: 0, cellWidth: 50, cellHeight: dm.minH });

  // Always-current ref so the ResizeObserver (deps=[]) sees latest gridData. Initialized null;
  // assigned below after gridData is computed.
  const gridDataRef = useRef<{ num_counters: number } | null>(null);
  // Full gridData ref for ResizeObserver + deferred renders (always points to latest).
  const fullGridDataRef = useRef<RosterGridData | null>(null);


  // ── Break slot tracking ───────────────────────────────────────────────────
  //
  // We maintain two independent maps, both persisted to sessionStorage so they
  // survive tab switches (component unmount/remount):
  //
  //  userBreakSlots   – slots the user explicitly chose "Break" via the popover.
  //                     Does NOT include auto-derived breaks (those live in the
  //                     gridData derivedBreaks computation below).
  //
  //  clearedBreakSlots – slots the user explicitly erased (break → empty/green).
  //                     These suppress derivedBreaks from re-appearing as "#"
  //                     even after schedule refreshes or tab switches.
  //
  // The storage key encodes shift type + officer IDs so different rosters in the
  // same browser session don't bleed into each other.

  const _breakStoreBase = `ac_brk_${shiftType}_${Object.keys(officer_schedules).sort().join(',')}`;

  const [userBreakSlots, setUserBreakSlots] = useState<Map<string, Set<number>>>(() => {
    try {
      const stored = sessionStorage.getItem(`${_breakStoreBase}_u`);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, number[]>;
        return new Map(Object.entries(parsed).map(([k, v]) => [k, new Set(v)]));
      }
    } catch { /* ignore storage errors */ }
    return new Map();
  });

  const [clearedBreakSlots, setClearedBreakSlots] = useState<Map<string, Set<number>>>(() => {
    try {
      const stored = sessionStorage.getItem(`${_breakStoreBase}_c`);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, number[]>;
        return new Map(Object.entries(parsed).map(([k, v]) => [k, new Set(v)]));
      }
    } catch { /* ignore storage errors */ }
    return new Map();
  });

  const prevOfficerIdsKeyRef = useRef<string>('');

  // Always-current refs so async callbacks (API calls) see latest values
  // without appearing in the dependency array of the break-protection effect.
  const userBreakSlotsRef = useRef(userBreakSlots);
  userBreakSlotsRef.current = userBreakSlots;
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;
  const shiftTypeRef = useRef(shiftType);
  shiftTypeRef.current = shiftType;

  // Persist userBreakSlots → sessionStorage on every change so tab switches don't lose data.
  useEffect(() => {
    try {
      const key = `ac_brk_${shiftTypeRef.current}_${Object.keys(officer_schedules).sort().join(',')}_u`;
      const obj: Record<string, number[]> = {};
      for (const [k, v] of userBreakSlots) if (v.size > 0) obj[k] = [...v];
      if (Object.keys(obj).length > 0) sessionStorage.setItem(key, JSON.stringify(obj));
      else sessionStorage.removeItem(key);
    } catch { /* ignore */ }
  }, [userBreakSlots, officer_schedules]);

  // Persist clearedBreakSlots → sessionStorage on every change.
  useEffect(() => {
    try {
      const key = `ac_brk_${shiftTypeRef.current}_${Object.keys(officer_schedules).sort().join(',')}_c`;
      const obj: Record<string, number[]> = {};
      for (const [k, v] of clearedBreakSlots) if (v.size > 0) obj[k] = [...v];
      if (Object.keys(obj).length > 0) sessionStorage.setItem(key, JSON.stringify(obj));
      else sessionStorage.removeItem(key);
    } catch { /* ignore */ }
  }, [clearedBreakSlots, officer_schedules]);

  // When the officer ID set changes (new roster loaded), remove departed officers
  // from both maps.  We do NOT re-derive breaks here: derivedBreaks in the gridData
  // useMemo already handles auto-detection from the schedule.
  useEffect(() => {
    const currentKey = Object.keys(officer_schedules).sort().join(',');
    if (currentKey === prevOfficerIdsKeyRef.current) return;
    prevOfficerIdsKeyRef.current = currentKey;

    const currentIds = new Set(Object.keys(officer_schedules));
    const cleanMap = (prev: Map<string, Set<number>>) => {
      const toRemove = [...prev.keys()].filter(id => !currentIds.has(id));
      if (toRemove.length === 0) return prev;
      const next = new Map(prev);
      for (const id of toRemove) next.delete(id);
      return next;
    };
    setClearedBreakSlots(cleanMap);
    setUserBreakSlots(cleanMap);
  }, [officer_schedules]);

  // Guard user-set breaks from being overwritten by bot auto-fill.
  // Only protects slots the user explicitly chose "Break" (userBreakSlots).
  // Auto-derived breaks are NOT protected — the bot may legitimately reassign them.
  // User-intentional counter overrides are safe because handleCounterAssign calls
  // updateBreaks(false) — removing the slot from userBreakSlots — before the refresh.
  useEffect(() => {
    const sh = getStartHour(shiftTypeRef.current);
    const toRevert: Array<{ officerId: string; start: string; end: string }> = [];
    for (const [offId, breakSlots] of userBreakSlotsRef.current) {
      const schedule = officer_schedules[offId] ?? [];
      for (const slot of breakSlots) {
        if ((schedule[slot] ?? 0) > 0) {
          toRevert.push({
            officerId: offId,
            start: slotToHmmm(slot, sh, 15),
            end: slotToHmmm(slot + 1, sh, 15),
          });
        }
      }
    }
    if (toRevert.length === 0) return;
    let active = true;
    // One request for every slot to revert: one version and one broadcast, not one each.
    apiClient
      .clearRanges(toRevert.map(({ officerId: offId, start, end }) => ({ officer_key: offId, start_time: start, end_time: end })))
      .then((result) => { if (active) onUpdatedRef.current?.(result); })
      .catch(() => { if (active) onUpdatedRef.current?.(); });
    return () => { active = false; };
  }, [officer_schedules]);

  // Build gridData with availability window markers
  const otColorVersion = useOtColorVersion(); // colours are baked into gridData
  const gridData = useMemo<RosterGridData>(() => {
    void otColorVersion; // dependency only: getOfficerColor reads the live OT colours
    const startHour = getStartHour(shiftType);
    const officerIds = orderOfficers(Object.keys(officer_schedules))
      .filter(id => !hiddenOfficerIds?.has(id));

    // Apply live availability override from the open edit popup for instant bracket preview.
    const effectiveOfficerMetadata: Record<string, OfficerMetadata> = (() => {
      if (!availabilityOverride || availabilityOverride.segs.length === 0) return officer_metadata;
      const { officerId: ovId, segs } = availabilityOverride;
      const original = officer_metadata[ovId];
      if (!original) return officer_metadata;
      return {
        ...officer_metadata,
        [ovId]: {
          ...original,
          availability_start: segs[0].start,
          availability_end: segs[segs.length - 1].end,
          availability_segments: segs.length > 1 ? segs : undefined,
        },
      };
    })();

    const counter_labels = officerIds.map((id) => {
      const schedule = officer_schedules[id] ?? [];
      const firstWorking = schedule.findIndex((v) => v > 0);
      const lastWorking = schedule.length - 1 - [...schedule].reverse().findIndex((v) => v > 0);
      const hasWork = firstWorking >= 0;
      const meta = effectiveOfficerMetadata[id];
      const nameLabel = officerLabel(id, meta);

      // Prefer stored availability window times over computed first/last working slot.
      // Multi-segment SOS/OT: show "start1–end1 · start2–end2"
      // Main officers: use stored availability_start/end (from original schedule, unaffected by edits)
      let timingLabel: string | null = null;
      if (meta?.availability_segments && meta.availability_segments.length > 1) {
        timingLabel = meta.availability_segments.map(s => `${s.start}–${s.end}`).join(' · ');
      } else if (meta?.availability_start && meta?.availability_end) {
        timingLabel = `${meta.availability_start}–${meta.availability_end}`;
      }
      if (!timingLabel && hasWork) {
        timingLabel = `${slotToHmmm(firstWorking, startHour, 15)}–${slotToHmmm(lastWorking + 1, startHour, 15)}`;
      }

      if (!timingLabel) return nameLabel;
      return `${nameLabel}\n${timingLabel}`;
    });

    const cells = officerIds.flatMap((officerId, row) => {
      const schedule = officer_schedules[officerId] ?? [];
      const meta = effectiveOfficerMetadata[officerId];
      const color = getOfficerColor(officerId, meta);

      // Availability slot ranges — needed by both the normal path below and the
      // availability-view override, so compute it once up front.
      const availRanges: Array<[number, number]> = [];
      if (meta?.availability_segments && meta.availability_segments.length > 1) {
        for (const seg of meta.availability_segments) {
          availRanges.push([hmmmToSlot(seg.start, startHour), hmmmToEndSlot(seg.end, startHour)]);
        }
      } else if (meta?.availability_start && meta?.availability_end) {
        availRanges.push([hmmmToSlot(meta.availability_start, startHour), hmmmToEndSlot(meta.availability_end, startHour)]);
      }

      // Availability-view mode: the counter layer dims to 50% instead of
      // disappearing — it stays visible for context (what's actually assigned
      // there) while the availability window being edited (brackets, drag
      // handles, the press-and-hold split/add popover) still reads as the
      // thing in focus.
      const inAvailEditMode = !!showAvailabilityRows?.has(officerId);

      const officerBreaks = userBreakSlots.get(officerId) ?? new Set<number>();
      const clearedSlots = clearedBreakSlots.get(officerId) ?? new Set<number>();
      const isSOSOT = meta && (meta.officer_type === 'SOS' || meta.officer_type === 'OT');
      // Red-font flag: a main officer's assignment (counter and/or its start/end
      // time) that no longer matches the initially-generated roster.
      const isMain = meta?.officer_type?.toUpperCase() === 'MAIN';
      const initialSchedule = initialOfficerSchedules?.[officerId];
      // A duty slot is working time: never a break, never green.
      const duties = dutySlots?.[officerId];
      const dutyAt = (s: number) => duties?.[String(s)];

      // Derive scheduled break slots for all officer types.
      // SOS/OT: short zero runs (< SHORT_BREAK_SLOTS) between counter assignments are
      //   breaks; longer gaps are availability windows.
      // Main officers: every unassigned slot strictly between first and last counter
      //   assignment is a break (continuous shift — no length threshold).
      // In both cases clearedBreakSlots suppresses individual slots, and
      // userBreakSlots can add breaks outside the auto-derived range.
      let derivedBreaks: Set<number> | undefined;
      // Working time is a counter or a free-text duty: a break between a counter and a duty is a break.
      const working = (v: number, s: number) => v > 0 || !!dutyAt(s);
      const first = schedule.findIndex((v, s) => working(v, s));
      if (first >= 0) {
        const last = schedule.length - 1 - [...schedule].reverse().findIndex((v, k) => working(v, schedule.length - 1 - k));
        const breaks = new Set<number>();
        if (isSOSOT) {
          let runStart = -1;
          for (let s = first + 1; s < last; s++) {
            const isZero = (schedule[s] ?? 0) === 0 && !dutyAt(s);
            if (isZero && runStart === -1) { runStart = s; }
            else if (!isZero && runStart !== -1) {
              if (s - runStart < SHORT_BREAK_SLOTS) for (let r = runStart; r < s; r++) breaks.add(r);
              runStart = -1;
            }
          }
          if (runStart !== -1 && last - runStart < SHORT_BREAK_SLOTS) {
            for (let r = runStart; r < last; r++) breaks.add(r);
          }
          // Deliberately no boundary-break pass here: a zero run at the very edge of an
          // availability segment (before the first counter or after the last) is never
          // "between two counters" — it's leading/trailing slack, e.g. from extending the
          // window — so it always reads as green availability, regardless of how short it
          // is. Only a gap genuinely sandwiched between two counter assignments (above) is
          // a scheduled break.
        } else {
          // Main officers: every unassigned slot between first and last counter is a break.
          for (let s = first + 1; s < last; s++) {
            if ((schedule[s] ?? 0) === 0 && !dutyAt(s)) breaks.add(s);
          }
        }
        if (breaks.size > 0) derivedBreaks = breaks;
      }

      // The breaks the roster PDF prints ("#"), read exactly: whatever the heuristics above infer, these are breaks.
      const printed = new Set(printedBreaks?.[officerId] ?? []);

      return Array.from({ length: num_slots }, (_, col) => {
        const counterNum = schedule[col] ?? 0;
        const assigned = counterNum > 0;
        const duty = assigned ? undefined : dutyAt(col);
        const inAvailWindow = availRanges.some(([s, e]) => col >= s && col < e);
        const isBreak = !assigned && !duty && !clearedSlots.has(col) && inAvailWindow && (
          officerBreaks.has(col) || printed.has(col) ||
          (derivedBreaks !== undefined && derivedBreaks.has(col))
        );
        const prevIsBreak = !clearedSlots.has(col - 1) && (
          officerBreaks.has(col - 1) || printed.has(col - 1) ||
          (derivedBreaks !== undefined && derivedBreaks.has(col - 1))
        );
        const isFirstBreakInRun = isBreak && !prevIsBreak;

        let cellColor: string;
        let cellText: string;

        if (assigned) {
          cellColor = color;
          cellText = formatCounterLabel(counterNum, operation_mode);
        } else if (duty) {
          // Drawn as a block like a counter's — border, start and end time, label — in gray.
          cellColor = DUTY_FILL;
          cellText = duty;
        } else if (isBreak) {
          cellColor = "#f0f0f0";
          cellText = isFirstBreakInRun ? `#\n${slotToHmmm(col, startHour, 15)}` : "#";
        } else if (inAvailWindow) {
          cellColor = AVAIL_UNASSIGNED_COLOR;
          cellText = "";
        } else {
          cellColor = "#f0f0f0";
          cellText = "";
        }

        return {
          row, col,
          officers: assigned || duty ? [officerId] : [],
          colors: assigned ? [color] : duty ? [DUTY_FILL] : [],
          color: cellColor,
          text: cellText,
          unavailable: false,
          counter: assigned ? counterNum : undefined,
          // Dim only the real counter assignment — breaks, the availability
          // highlight and empty background all stay at full strength, since
          // the window being edited is what should keep reading as "in focus".
          opacity: inAvailEditMode && assigned ? 0.5 : undefined,
          changed: isMain && assigned && (initialSchedule?.[col] ?? 0) !== counterNum,
          // A note can annotate any slot inside the availability window — assigned,
          // on a break, or plain unassigned — not just counter-assigned ones.
          // JSON object keys always arrive as strings, even for a numeric slot index.
          note: (assigned || inAvailWindow || duty) ? assignmentNotes?.[officerId]?.[String(col)] : undefined,
          duty,
        };
      });
    });

    // Availability brackets: colored left/right borders at the edges of each
    // SOS/OT officer's availability window, drawn on top of all cells/segments.
    // Main officers also get a bracket in deep blue.
    const SOS_BRACKET = '#e879a0';
    const OT_BRACKET  = '#f59e0b';
    const availability_brackets: AvailabilityBracket[] = officerIds.flatMap((officerId, row) => {
      const meta = effectiveOfficerMetadata[officerId];
      const isSOSOT = meta && (meta.officer_type === 'SOS' || meta.officer_type === 'OT');
      const isMain  = meta?.officer_type?.toUpperCase() === 'MAIN';
      const bracketColor = meta?.officer_type === 'SOS' ? SOS_BRACKET
                         : meta?.officer_type === 'OT'  ? OT_BRACKET
                         : '#1d4ed8'; // Main: deep blue

      if (isSOSOT) {
        if (meta.availability_segments && meta.availability_segments.length > 1) {
          return meta.availability_segments.map(seg => ({
            row,
            startCol: hmmmToSlot(seg.start, startHour),
            endCol:   hmmmToEndSlot(seg.end, startHour) - 1,
            color:    bracketColor,
          }));
        }
        if (meta.availability_start && meta.availability_end) {
          const startCol = hmmmToSlot(meta.availability_start, startHour);
          const endCol   = hmmmToEndSlot(meta.availability_end, startHour) - 1;
          return [{ row, startCol, endCol, color: bracketColor }];
        }
        // Fallback: derive from assignment range
        const schedule = officer_schedules[officerId] ?? [];
        const firstWorking = schedule.findIndex((v) => v > 0);
        if (firstWorking < 0) return [];
        const lastWorking = schedule.length - 1 - [...schedule].reverse().findIndex((v) => v > 0);
        return [{ row, startCol: firstWorking, endCol: lastWorking, color: bracketColor }];
      }

      if (isMain && meta?.availability_start && meta?.availability_end) {
        try {
          const startCol = hmmmToSlot(meta.availability_start, startHour);
          const endCol   = hmmmToEndSlot(meta.availability_end, startHour) - 1;
          return [{ row, startCol, endCol, color: bracketColor }];
        } catch { return []; }
      }

      return [];
    });

    return { num_counters: officerIds.length, num_slots, cells, time_labels, counter_labels, officer_metadata, availability_brackets };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officer_schedules, officer_metadata, time_labels, num_slots, operation_mode, shiftType, userBreakSlots, clearedBreakSlots, hiddenOfficerIds, availabilityOverride, showAvailabilityRows, initialOfficerSchedules, assignmentNotes, dutySlots, printedBreaks, otColorVersion, customOrder]);

  // Keep refs current so ResizeObserver closure (deps=[]) can read latest data.
  gridDataRef.current = gridData;
  fullGridDataRef.current = gridData;

  const sortedOfficerIds = useMemo(
    () => orderOfficers(Object.keys(officer_schedules)).filter(id => !hiddenOfficerIds?.has(id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [officer_schedules, hiddenOfficerIds, customOrder],
  );

  // Per-officer row heights derived from label content (name length + whether time is shown).
  const baseRowHeights = useMemo(() => {
    const startHourLocal = getStartHour(shiftType);
    const bounds = SHIFT_BOUNDS[shiftType] ?? SHIFT_BOUNDS.morning;
    return sortedOfficerIds.map(officerId => {
      const meta = officer_metadata[officerId];
      const schedule = officer_schedules[officerId] ?? [];
      const firstWorking = schedule.findIndex((v) => v > 0);
      const hasWork = firstWorking >= 0;
      const lastWorking = hasWork
        ? schedule.length - 1 - [...schedule].reverse().findIndex((v) => v > 0)
        : -1;
      const isSOSOT = meta
        ? (meta.officer_type === 'SOS' || meta.officer_type === 'OT')
        : (officerId.startsWith('OT') || (officerId.startsWith('S') && !officerId.startsWith('SN')));
      const availStart = meta?.availability_start || null;
      const availEnd   = meta?.availability_end   || null;
      const assignStart = hasWork ? slotToHmmm(firstWorking, startHourLocal, 15) : null;
      const assignEnd   = hasWork ? slotToHmmm(lastWorking + 1, startHourLocal, 15) : null;
      let timeDisplay: string | null = null;
      if (meta?.availability_segments && meta.availability_segments.length > 1) {
        timeDisplay = meta.availability_segments.map(s => `${fmtTime(s.start)}–${fmtTime(s.end)}`).join(' · ');
      } else if (availStart && availEnd) {
        timeDisplay = `${fmtTime(availStart)}–${fmtTime(availEnd)}`;
      } else if (assignStart && assignEnd) {
        timeDisplay = `${fmtTime(assignStart)}–${fmtTime(assignEnd)}`;
      }
      let hasTimeLabel = false;
      let extraTimeLines = 0;
      const excl = !isSOSOT ? mainExclusionText(meta, startHourLocal, availStart, availEnd, assignStart, assignEnd) : null;
      if (excl) {
        // The windows line and the duty line, each wrapping at the label's width.
        hasTimeLabel = true;
        extraTimeLines = wrappedLineCount(excl.windows, dm.textW, dm.timeFont) - 1 + wrappedLineCount(excl.duty, dm.textW, dm.timeFont);
      } else if (isSOSOT && timeDisplay) {
        hasTimeLabel = true;
      } else if (!isSOSOT && timeDisplay) {
        const effStart = availStart || assignStart;
        const effEnd   = availEnd   || assignEnd;
        if (!(effStart === bounds.start && effEnd === bounds.end)) hasTimeLabel = true;
      }
      return estimateOfficerRowH(dm, officerLabel(officerId, meta), hasTimeLabel, extraTimeLines);
    });
  }, [sortedOfficerIds, officer_metadata, officer_schedules, shiftType, dm]);

  // A row holding a note on a cell too narrow to show it is one line taller, and the note sits in
  // that line (see canvas/noteLane). The canvas decides which rows and how much — from the same
  // segments it draws — and the row heights here, which the label column and the rest of the
  // page also follow, have to include it. Needs the canvas, so it settles once that has mounted.
  const [rendererVersion, setRendererVersion] = useState(0);
  const noteLane = useMemo(
    () => (rendererRef.current ? rendererRef.current.noteLaneLayout(gridData, layoutInfo.cellWidth) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridData, layoutInfo.cellWidth, layoutInfo.cellHeight, rendererVersion],
  );
  const officerRowHeights = useMemo(
    () => (noteLane && noteLane.lineH > 0
      ? baseRowHeights.map((h, i) => h + (rendererRef.current?.noteLaneExtra(h, noteLane.tracks.get(i) ?? 0, noteLane.lineH) ?? 0))
      : baseRowHeights),
    [baseRowHeights, noteLane],
  );

  // ── Availability-view editing (Hide All CTR mode) ──────────────────────────
  //
  // officer_metadata.availability_segments (or availability_start/end for a
  // single window) is the single source of truth for availability — read here,
  // written via apiClient.updateAvailability, and picked up automatically by
  // every other view (officer card, y-labels) once onUpdated triggers a refetch.

  const getAvailSegs = (officerId: string): AvailWindow[] => {
    // Read through the same instant-preview override the canvas renders from —
    // otherwise a second action fired right after the first (before the network
    // round-trip lands) would compute against stale, already-superseded segments.
    if (availabilityOverride?.officerId === officerId) return availabilityOverride.segs;
    return officerWindows(officer_metadata[officerId]);
  };

  const [pressPopover, setPressPopover] = useState<{
    officerId: string; col: number; x: number; y: number; kind: 'split' | 'add' | 'merge';
    /** Only set for kind 'merge' — index of the first of the two adjacent segments to merge. */
    mergeSegIndex?: number;
  } | null>(null);
  const persistAvailability = async (officerId: string, newSegs: AvailWindow[]) => {
    if (newSegs.length === 0) return;
    const sh = getStartHour(shiftType);
    const span = timingSpan(newSegs.map(s => `${s.start}-${s.end}`).join(','), sh);
    if (!span) return;
    // Show the result immediately — onUpdated's refetch is a real network round-trip
    // that a slow/real deployment can take a visible moment to complete.
    onAvailabilityPreview?.(officerId, newSegs);
    const result = await apiClient.updateAvailability({
      officer_key: officerId,
      availability_start: span.start,
      availability_end: span.end,
      segments: newSegs.length > 1 ? newSegs : undefined,
    });
    onUpdated?.(result);
  };

  const splitWindowAt = async (officerId: string, col: number) => {
    const sh = getStartHour(shiftType);
    const segs = getAvailSegs(officerId);
    const segIndex = segs.findIndex(s => {
      try { const ss = hmmmToSlot(s.start, sh); const se = hmmmToEndSlot(s.end, sh); return col >= ss && col < se; } catch { return false; }
    });
    if (segIndex < 0) return;
    const seg = segs[segIndex];
    let ss: number, se: number;
    try { ss = hmmmToSlot(seg.start, sh); se = hmmmToEndSlot(seg.end, sh); } catch { return; }
    // Valid split points are [ss, se-2]: win1=[ss,col+1) and win2=[col+1,se) must both be
    // non-empty. col===ss IS a valid split point (win1 ends up 1 slot wide) — rejecting it
    // was an off-by-one that made a 2-slot window (only col===ss is valid there) unsplittable.
    if (col < ss || col >= se - 1) return;
    const boundary = slotToHmmm(col + 1, sh, 15);
    const win1: AvailWindow = { start: seg.start, end: boundary };
    const win2: AvailWindow = { start: boundary, end: seg.end };
    const nextSegs = [...segs.slice(0, segIndex), win1, win2, ...segs.slice(segIndex + 1)];
    await persistAvailability(officerId, nextSegs); // splitting never removes coverage — no counter deletion needed
  };

  const addWindowAt = async (officerId: string, col: number) => {
    const sh = getStartHour(shiftType);
    const segs = getAvailSegs(officerId);
    const inside = segs.some(s => {
      try { const ss = hmmmToSlot(s.start, sh); const se = hmmmToEndSlot(s.end, sh); return col >= ss && col < se; } catch { return false; }
    });
    if (inside) return;
    const DEFAULT_WINDOW_SLOTS = 4; // 1 hour
    let endSlot = Math.min(col + DEFAULT_WINDOW_SLOTS, num_slots);
    for (const s of segs) {
      try {
        const ss = hmmmToSlot(s.start, sh);
        if (ss > col && ss < endSlot) endSlot = ss;
      } catch { /* skip invalid segment */ }
    }
    if (endSlot <= col) return;
    const newWin: AvailWindow = { start: slotToHmmm(col, sh, 15), end: slotToHmmm(endSlot, sh, 15) };
    await persistAvailability(officerId, mergeAvailabilitySegments([...segs, newWin], sh));
  };

  // Touch-target tolerance (Apple's HIG floor is 44pt) used when hit-testing
  // a press against a shared window boundary — see findMergeableBoundary.
  const TOUCH_TARGET_PX = 44;
  const boundaryTolerance = (): number =>
    Math.max(0, Math.round((TOUCH_TARGET_PX / layoutInfo.cellWidth - 1) / 2));

  /**
   * If `col` is close enough to the shared boundary between two ADJACENT
   * (touching, no gap) availability windows to count as pressing it, returns
   * the index of the first of the two — the pair a "Merge windows" press
   * should combine. Null otherwise, including at a real gap between windows
   * or the officer's outermost start/end (there's no "other side" to merge
   * into there — that's a plain split/add boundary instead).
   */
  const findMergeableBoundary = (officerId: string, col: number): number | null => {
    const sh = getStartHour(shiftType);
    const segs = getAvailSegs(officerId);
    const tolerance = boundaryTolerance();
    for (let i = 0; i < segs.length - 1; i++) {
      try {
        const se = hmmmToEndSlot(segs[i].end, sh) - 1;
        const nextSs = hmmmToSlot(segs[i + 1].start, sh);
        if (se + 1 !== nextSs) continue; // a real gap — not a merge boundary
        if (Math.abs(col - se) <= tolerance || Math.abs(col - nextSs) <= tolerance) return i;
      } catch { /* skip invalid segment */ }
    }
    return null;
  };

  const mergeWindowsAt = async (officerId: string, segIndex: number) => {
    const segs = getAvailSegs(officerId);
    const a = segs[segIndex];
    const b = segs[segIndex + 1];
    if (!a || !b) return;
    const merged: AvailWindow = { start: a.start, end: b.end };
    const nextSegs = [...segs.slice(0, segIndex), merged, ...segs.slice(segIndex + 2)];
    await persistAvailability(officerId, nextSegs); // merging never removes coverage — no counter deletion needed
  };

  /**
   * Shared entry point for every click/press on a row currently in (or eligible
   * to enter) availability-view mode. Called from both the mouse drag-select
   * path and the click callback (which also carries touch taps) so the
   * behavior is identical regardless of input device. Returns true when it
   * handled the interaction — callers should stop their own default handling.
   */
  const handleAvailModeInteractionRef = useRef<
    (row: number, col: number, gesture: 'click' | 'drag', x: number, y: number) => boolean
  >(() => false);
  handleAvailModeInteractionRef.current = (row, col, gesture, x, y) => {
    const officerId = sortedOfficerIds[row];
    if (!officerId) return false;
    const sh = getStartHour(shiftType);

    if (showAvailabilityRows?.has(officerId)) {
      // A multi-cell mouse drag (the desktop stand-in for touch long-press) paints a
      // blue drag-highlight tint via the canvas's own mousemove handling; since this
      // gesture never reaches setDragSelection, nothing else repaints the row — do it
      // here so the row goes back to its plain green/gray look immediately.
      if (rendererRef.current && gridData) rendererRef.current.render(gridData);
      if (gesture === 'drag') {
        const mergeSegIndex = findMergeableBoundary(officerId, col);
        if (mergeSegIndex !== null) {
          onInteraction?.();
          setPressPopover({ officerId, col, x, y, kind: 'merge', mergeSegIndex });
        } else {
          const segs = getAvailSegs(officerId);
          const inWindow = segs.some(s => {
            try { const ss = hmmmToSlot(s.start, sh); const se = hmmmToEndSlot(s.end, sh); return col >= ss && col < se; } catch { return false; }
          });
          onInteraction?.();
          setPressPopover({ officerId, col, x, y, kind: inWindow ? 'split' : 'add' });
        }
      } else {
        // A plain tap (not a press-and-hold) while in the availability layer
        // opens the officer's full edit popup — layer entry/exit itself is
        // purely the global "<>" toggle now, never a tap on the grid.
        const meta = officer_metadata[officerId];
        const startTime = slotToHmmm(col, sh, 15);
        onInteraction?.();
        onOfficerClick?.({ officer: officerId, counter: 'green', time: `${startTime}-${startTime}`, metadata: meta } as HoverInfo);
      }
      return true;
    }

    return false;
  };

  const totalContentHeight = useMemo(() => officerRowHeights.reduce((a, b) => a + b, 0), [officerRowHeights]);

  // ── Drag a row to a new position ──────────────────────────────────────────
  // Driven by the row's grip handle with Pointer Events, so one code path covers
  // mouse, pen and touch (iPad / iOS / Android). The handle has touch-action:none,
  // so a finger on it drags the row while a finger anywhere else still scrolls.
  const labelListRef = useRef<HTMLDivElement>(null);
  const [rowDrag, setRowDrag] = useState<{ id: string; slot: number; x: number; y: number } | null>(null);
  const rowDragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => rowDragCleanupRef.current?.(), []);

  const startRowDrag = (e: React.PointerEvent, officerId: string) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    onInteraction?.();
    rowDragCleanupRef.current?.();
    const pointerId = e.pointerId;
    const ids = sortedOfficerIds;
    const heights = officerRowHeights;
    const srcIdx = ids.indexOf(officerId);
    if (srcIdx < 0) return;
    const ptr = { x: e.clientX, y: e.clientY };

    // Insertion slot 0..n: the gap nearest the pointer (row midpoints are the cut-offs).
    const slotAt = (clientY: number): number => {
      const top = labelListRef.current?.getBoundingClientRect().top ?? 0;
      let y = top;
      for (let i = 0; i < ids.length; i++) {
        const h = heights[i] ?? 0;
        if (clientY < y + h / 2) return i;
        y += h;
      }
      return ids.length;
    };
    const update = () => setRowDrag({ id: officerId, slot: slotAt(ptr.y), x: ptr.x, y: ptr.y });

    // Edge auto-scroll so a destination off-screen is reachable mid-drag.
    let raf = 0;
    const tick = () => {
      const EDGE = 56, MAX = 18;
      const panel = gridPanelRef.current;
      const vh = window.innerHeight;
      const canScrollPanel = !!panel && panel.scrollHeight > panel.clientHeight + 1;
      const r = canScrollPanel ? panel!.getBoundingClientRect() : null;
      const topEdge = Math.max(0, r?.top ?? 0), bottomEdge = Math.min(vh, r?.bottom ?? vh);
      let dy = 0;
      if (ptr.y < topEdge + EDGE) dy = -Math.ceil(MAX * Math.min(1, (topEdge + EDGE - ptr.y) / EDGE));
      else if (ptr.y > bottomEdge - EDGE) dy = Math.ceil(MAX * Math.min(1, (ptr.y - (bottomEdge - EDGE)) / EDGE));
      if (dy) {
        if (canScrollPanel) panel!.scrollTop += dy; else window.scrollBy(0, dy);
        update();
      }
      raf = requestAnimationFrame(tick);
    };

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      ptr.x = ev.clientX; ptr.y = ev.clientY;
      update();
    };
    const finish = (commit: boolean) => {
      cleanup();
      const target = slotAt(ptr.y);
      setRowDrag(null);
      if (!commit) return;
      const to = target > srcIdx ? target - 1 : target;   // index after removing the dragged row
      if (to === srcIdx) return;
      const visible = ids.filter(id => id !== officerId);
      const full = orderOfficers(Object.keys(officer_schedules)).filter(id => id !== officerId);
      const anchor = visible[to];                          // row the dragged one lands in front of
      const at = anchor === undefined ? full.length : full.indexOf(anchor);
      full.splice(at, 0, officerId);
      saveCustomOrder(full);
    };
    const onUp = (ev: PointerEvent) => { if (ev.pointerId === pointerId) finish(true); };
    const onCancel = (ev: PointerEvent) => { if (ev.pointerId === pointerId) finish(false); };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') finish(false); };
    const block = (ev: Event) => ev.preventDefault();      // no page scroll / context menu while dragging
    const cleanup = () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('touchmove', block);
      window.removeEventListener('contextmenu', block);
      rowDragCleanupRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey);
    window.addEventListener('touchmove', block, { passive: false });
    window.addEventListener('contextmenu', block);
    rowDragCleanupRef.current = cleanup;
    raf = requestAnimationFrame(tick);
    update();
  };

  // Where the drop line sits, measured from the top of the first row.
  const rowDragLineTop = useMemo(() => {
    if (!rowDrag) return null;
    return officerRowHeights.slice(0, rowDrag.slot).reduce((a, b) => a + b, 0);
  }, [rowDrag, officerRowHeights]);
  const rowDragSrcIdx = rowDrag ? sortedOfficerIds.indexOf(rowDrag.id) : -1;
  const rowDragSrcTop = rowDragSrcIdx >= 0 ? officerRowHeights.slice(0, rowDragSrcIdx).reduce((a, b) => a + b, 0) : 0;
  // Dropping in front of itself or the next row changes nothing — draw the line muted then.
  const rowDragNoop = !!rowDrag && (rowDrag.slot === rowDragSrcIdx || rowDrag.slot === rowDragSrcIdx + 1);

  // ── Officer filter panel ──────────────────────────────────────────────────
  const [filterOpen, setFilterOpen] = useState(false);
  const filterDrag = useDragOffset();
  useEffect(() => { if (!filterOpen) filterDrag.reset(); }, [filterOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const [filterAnchor, setFilterAnchor] = useState<{ top: number; left: number } | null>(null);
  const [filterSearchText, setFilterSearchText] = useState('');
  const cornerBtnRef = useRef<HTMLButtonElement>(null);

  // ── Officer deletion: per-row trash icon, or Edit mode with multi-select ──
  const { notifySuccess, notifyError, notifyWarning } = useNotification();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<string[] | null>(null);
  const dialogScale = useDialogScale();

  const exitEditMode = () => { setEditMode(false); setSelectedIds(new Set()); selectAnchorRef.current = null; };
  const toggleSelected = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Fast multi-select ──
  // Computer: Ctrl/Cmd+click a row to start selecting (no need to press Edit first),
  // Shift+click to take every row from the last one clicked, Ctrl/Cmd+A for all,
  // Delete to remove, Esc to leave. Phone/tablet: press and hold a row to start
  // selecting, then tap more rows (or "Select all"). Edit mode is entered
  // automatically by any of these.
  const selectAnchorRef = useRef<string | null>(null);
  const longPressRef = useRef<{ timer: number; x: number; y: number } | null>(null);
  const suppressNextClickRef = useRef(false);

  const selectRow = (id: string, mods: { shiftKey?: boolean } = {}) => {
    setEditMode(true);
    const anchor = selectAnchorRef.current;
    if (mods.shiftKey && anchor && anchor !== id) {
      const a = sortedOfficerIds.indexOf(anchor);
      const b = sortedOfficerIds.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSelectedIds(prev => new Set([...prev, ...sortedOfficerIds.slice(lo, hi + 1)]));
        selectAnchorRef.current = id;
        return;
      }
    }
    toggleSelected(id);
    selectAnchorRef.current = id;
  };

  // ── Swipe a row (any mode): left far enough deletes it, right far enough hides it ──
  // The row follows the finger by direct style writes (no re-render per pixel) over a coloured strip
  // that turns solid and says "Release" once the pull is far enough. Letting go past that point opens
  // the usual "Delete officer?" confirmation (left) or hides the row (right — it comes back from the
  // Filter button); letting go short of it just springs back.
  const swipeRef = useRef<{ id: string; el: HTMLElement; x: number; y: number; dx: number; live: boolean } | null>(null);
  const swipeLimits = (el: HTMLElement) => ({ max: el.offsetWidth * SWIPE_MAX_FRACTION, arm: el.offsetWidth * SWIPE_ARM_FRACTION });
  /** Clamp a raw drag distance: left always, right only where rows can be hidden. */
  const clampSwipe = (dx: number, max: number) => Math.max(-max, Math.min(onChangeHiddenIds ? max : 0, dx));
  const paintSwipeStrip = (el: HTMLElement, dx: number, arm: number) => {
    const strip = el.previousElementSibling as HTMLElement | null;
    if (!strip || strip.dataset.swipeStrip === undefined) return;
    const armed = Math.abs(dx) >= arm;
    for (const layer of Array.from(strip.children) as HTMLElement[]) {
      const side = layer.dataset.swipeSide;
      const showing = dx !== 0 && (side === 'delete') === (dx < 0);
      layer.style.display = showing ? 'flex' : 'none';
      layer.style.background = armed ? (side === 'delete' ? '#dc2626' : '#4f46e5') : (side === 'delete' ? '#fca5a5' : '#a5b4fc');
      const label = layer.querySelector('[data-swipe-label]');
      if (label) label.textContent = armed ? 'Release' : side === 'delete' ? 'Delete' : 'Hide';
    }
  };
  const startSwipe = (e: React.PointerEvent<HTMLElement>, id: string) => {
    if ((e.target as HTMLElement).closest('button, [role="button"]')) return;
    swipeRef.current = { id, el: e.currentTarget, x: e.clientX, y: e.clientY, dx: 0, live: false };
  };
  const moveSwipe = (e: React.PointerEvent<HTMLElement>) => {
    const sw = swipeRef.current;
    if (!sw) return;
    const raw = e.clientX - sw.x;
    if (!sw.live) {
      if (Math.abs(raw) < 8 || Math.abs(raw) < Math.abs(e.clientY - sw.y)) return;
      sw.live = true;
      cancelLongPress();
      suppressNextClickRef.current = true;
      sw.el.style.transition = 'none';
      try { sw.el.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    }
    const { max, arm } = swipeLimits(sw.el);
    sw.dx = clampSwipe(raw, max);
    sw.el.style.transform = `translateX(${sw.dx}px)`;
    paintSwipeStrip(sw.el, sw.dx, arm);
  };
  const endSwipe = (e: React.PointerEvent<HTMLElement>) => {
    const sw = swipeRef.current;
    swipeRef.current = null;
    if (!sw || !sw.live) return;
    const { arm } = swipeLimits(sw.el);
    sw.el.style.transition = '';
    sw.el.style.transform = 'translateX(0px)';
    paintSwipeStrip(sw.el, 0, arm);
    // A cancelled gesture (the browser took the scroll) is not a release.
    if (e.type === 'pointerup' && Math.abs(sw.dx) >= arm) {
      if (sw.dx < 0) setPendingDelete([sw.id]);
      else if (onChangeHiddenIds) {
        const next = new Set(hiddenOfficerIds);
        next.add(sw.id);
        onChangeHiddenIds(next);
        notifySuccess(`Hid ${sw.id} — bring it back from Filter`);
      }
    }
    // The browser still sends a click after the drag; the swipe has already been handled.
    window.setTimeout(() => { suppressNextClickRef.current = false; }, 0);
  };

  // First visit: the first row slides open and back once, so the gesture is discoverable without
  // adding any permanent clutter to the labels.
  const [swipeHint, setSwipeHint] = useState(false);
  useEffect(() => {
    if (loadPref(SWIPE_HINT_KEY)) return;
    savePref(SWIPE_HINT_KEY, '1');
    setSwipeHint(true);
  }, []);

  const cancelLongPress = () => {
    if (longPressRef.current) { window.clearTimeout(longPressRef.current.timer); longPressRef.current = null; }
  };
  const startLongPress = (e: React.PointerEvent, id: string) => {
    if (e.pointerType === 'mouse' || editMode) return;
    cancelLongPress();
    longPressRef.current = {
      x: e.clientX, y: e.clientY,
      timer: window.setTimeout(() => {
        longPressRef.current = null;
        suppressNextClickRef.current = true;
        selectRow(id);
        try { navigator.vibrate?.(15); } catch { /* not supported (iOS) */ }
      }, 450),
    };
  };
  const moveLongPress = (e: React.PointerEvent) => {
    const lp = longPressRef.current;
    if (lp && Math.hypot(e.clientX - lp.x, e.clientY - lp.y) > 8) cancelLongPress();
  };

  // Keyboard while selecting. Refs keep the handler current without re-binding.
  const kbRef = useRef({ selectedIds, pendingDelete, sortedOfficerIds });
  kbRef.current = { selectedIds, pendingDelete, sortedOfficerIds };
  useEffect(() => {
    if (!editMode) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable) && (t as HTMLInputElement).type !== 'checkbox') return;
      const { selectedIds: sel, pendingDelete: pd, sortedOfficerIds: ids } = kbRef.current;
      if (pd) { if (e.key === 'Escape') setPendingDelete(null); return; }
      if (e.key === 'Escape') { exitEditMode(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectedIds(new Set(ids));
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && sel.size > 0) {
        e.preventDefault();
        setPendingDelete(ids.filter(id => sel.has(id)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode]);
  useEffect(() => cancelLongPress, []);

  // One batched request for any number of officers. The grid is cleared
  // optimistically (rolled back on failure) so the removal feels instant even
  // though the server still has to rebuild the roster history.
  const confirmDelete = async () => {
    const ids = pendingDelete;
    if (!ids || ids.length === 0) return;
    setPendingDelete(null);
    exitEditMode();
    onInteraction?.();
    const rollback = onOptimisticRemove?.(ids);
    if (hiddenOfficerIds && onChangeHiddenIds && ids.some(id => hiddenOfficerIds.has(id))) {
      const next = new Set(hiddenOfficerIds);
      ids.forEach(id => next.delete(id));
      onChangeHiddenIds(next);
    }
    try {
      const result = await apiClient.removeOfficers(ids);
      notifySuccess(ids.length === 1 ? `Removed ${ids[0]}` : `Removed ${ids.length} officers`);
      onUpdated?.(result);
    } catch (err) {
      rollback?.();
      notifyError(`Failed to remove officer${ids.length > 1 ? 's' : ''}: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const allOfficerIds = useMemo(
    () => naturalSortOfficers(Object.keys(officer_schedules)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [officer_schedules, customOrder],
  );

  const officerGroups = useMemo(() => {
    const byType: Record<string, string[]> = { MAIN: [], SOS: [], OT: [] };
    for (const id of allOfficerIds) {
      const t = (officer_metadata[id]?.officer_type ?? '').toUpperCase();
      if (t === 'MAIN') byType.MAIN.push(id);
      else if (t === 'OT' || id.startsWith('OT')) byType.OT.push(id);
      else byType.SOS.push(id);
    }
    return (['MAIN', 'SOS', 'OT'] as const).flatMap(type => {
      const ids = byType[type];
      if (!ids.length) return [];
      const batches: Array<{ ids: string[]; label: string }> = [];
      for (let i = 0; i < ids.length; i += 10) {
        const slice = ids.slice(i, i + 10);
        batches.push({ ids: slice, label: `${slice[0]}–${slice[slice.length - 1]}` });
      }
      const typeLabel = type === 'MAIN' ? 'Main' : type === 'SOS' ? 'SOS' : 'OT';
      return [{ type: type as 'MAIN' | 'SOS' | 'OT', label: typeLabel, ids, batches }];
    });
  }, [allOfficerIds, officer_metadata]);

  const officerMatchesFilterSearch = (id: string, q: string): boolean => {
    if (!q) return true;
    const m = officer_metadata[id];
    const nameLabel = [m?.deployment, m?.name].filter(Boolean).join(' - ').toLowerCase();
    return id.toLowerCase().includes(q) || nameLabel.includes(q);
  };

  const handleFilterToggle = () => {
    if (filterOpen) {
      setFilterOpen(false);
      setFilterAnchor(null);
      setFilterSearchText('');
    } else {
      const rect = cornerBtnRef.current?.getBoundingClientRect();
      if (rect) setFilterAnchor({ top: rect.bottom + 2, left: rect.left });
      setFilterOpen(true);
    }
  };

  // ── Canvas callbacks ─────────────────────────────────────────────────────
  const onDragSelectRef = useRef<
    (row: number, startCol: number, endCol: number, x: number, y: number, mode: 'add' | 'erase', gesture: 'click' | 'drag') => void
  >(() => {});

  /**
   * A tap (mouse click or touch) on a free slot of an officer's row: inside their window it opens their card at
   * that time; outside it, nothing can go there, so say so — with the windows, and how to add one — rather than
   * leave the tap looking broken.
   */
  const openOrRefuseFreeSlot = (officerId: string, col: number) => {
    const sh = getStartHour(shiftType);
    const meta = officer_metadata[officerId];
    const windows = getAvailSegs(officerId);
    if (!slotInWindows(col, windows, sh)) {
      const who = meta?.name || officerId;
      notifyWarning(`${slotToHmmm(col, sh, 15)} is outside ${who}'s availability (${windowsText(windows)}). Press and hold to assign a counter there — the window widens to fit.`);
      return;
    }
    const startTime = slotToHmmm(col, sh, 15);
    onInteraction?.();
    onOfficerClick?.({ officer: officerId, counter: 'green', time: `${startTime}-${startTime}`, metadata: meta } as HoverInfo);
  };

  onDragSelectRef.current = (row, startCol, endCol, x, y, mode, gesture) => {
    const officerId = sortedOfficerIds[row];
    if (!officerId) return;

    // Availability-view mode (or entering it via a boundary-marker click) takes
    // priority over the normal counter-assign flow below.
    if (handleAvailModeInteractionRef.current(row, startCol, gesture, x, y)) return;

    // Single mouse-click (not a long-press) on a free cell → the officer's card, or why not (openOrRefuseFreeSlot).
    // Long-press ('drag' gesture) opens the CounterAssignPopover so the user can assign a counter or break,
    // matching the interaction hint text.
    if (startCol === endCol && mode === 'add' && gesture !== 'drag') {
      const schedule = officer_schedules[officerId] ?? [];
      if ((schedule[startCol] ?? 0) === 0) {
        openOrRefuseFreeSlot(officerId, startCol);
        return;
      }
    }
    const startHour = getStartHour(shiftType);
    const startTime = slotToHmmm(startCol, startHour, 15);
    const endTime = slotToHmmm(endCol + 1, startHour, 15);
    onInteraction?.();
    setDragSelection({ row, officerId, startTime, endTime, x, y, mode });
  };

  const stableDragSelect = useMemo(
    () => (row: number, startCol: number, endCol: number, x: number, y: number, mode: 'add' | 'erase', gesture: 'click' | 'drag') =>
      onDragSelectRef.current(row, startCol, endCol, x, y, mode, gesture),
    [],
  );

  const handleCanvasClickRef = useRef<(info: HoverInfo) => void>(() => {});
  handleCanvasClickRef.current = (info: HoverInfo) => {
    // Covers touch taps (any cell, officer or not) and mouse clicks on assigned
    // cells that also happen to land on a boundary/availability-view row.
    if (handleAvailModeInteractionRef.current(info.row, info.col, 'click', info.clientX ?? 0, info.clientY ?? 0)) return;
    if (info.officer) {
      onInteraction?.();
      onOfficerClick?.(info);
      return;
    }
    // Touch short-tap on a green (available/unassigned) cell: open officer edit popup.
    // Mouse single-clicks on green cells are handled via onDragSelectRef (startCol===endCol path).
    const officerId = sortedOfficerIds[info.row];
    if (!officerId) return;
    const schedule = officer_schedules[officerId] ?? [];
    if ((schedule[info.col] ?? 0) !== 0) return; // assigned — shouldn't reach here but guard
    openOrRefuseFreeSlot(officerId, info.col);
  };
  const stableCanvasClick = useMemo(() => (info: HoverInfo) => handleCanvasClickRef.current(info), []);

  // ── Counter assign / erase ────────────────────────────────────────────────
  /** What the selection starts on, for the hold pop-up to open on: its counter, or its duty. */
  const choiceAtSelection = (sel: { officerId: string; startTime: string }): string | undefined => {
    let slot: number;
    try { slot = hmmmToSlot(sel.startTime, getStartHour(shiftType)); } catch { return undefined; }
    const counter = officer_schedules[sel.officerId]?.[slot] ?? 0;
    const duty = dutySlots?.[sel.officerId]?.[String(slot)];
    return counter > 0 || duty ? choiceOf({ counter: String(counter), duty }) : undefined;
  };

  /** What the hold pop-up chose for the selected range (see CounterAssignPopover). */
  const handleCellAction = async (action: CellAction) => {
    if (!dragSelection) return;
    const { officerId, startTime, endTime } = dragSelection;
    const meta = officer_metadata[officerId];
    const startHour = getStartHour(shiftType);
    const startSlot = hmmmToSlot(startTime, startHour);
    const endSlotExcl = hmmmToEndSlot(endTime, startHour);

    const updateBreaks = (add: boolean) => {
      setUserBreakSlots((prev) => {
        const next = new Map(prev);
        const slots = new Set(prev.get(officerId) ?? []);
        for (let s = startSlot; s < endSlotExcl; s++) {
          if (add) slots.add(s); else slots.delete(s);
        }
        next.set(officerId, slots);
        return next;
      });
    };

    let result: OperationResult | undefined;
    // Clear the grid RIGHT NOW instead of waiting on the delete/statistics round
    // trip — rolled back on failure. The popover itself stays open until the
    // request settles (same as before), so an error still has somewhere to show.
    let rollback: (() => void) | undefined;
    try {
      if (action.kind === 'clear') {
        rollback = onOptimisticClear?.([{ officerKey: officerId, startSlot, endSlot: endSlotExcl }]);
        result = await apiClient.deleteOfficer({ officer: officerId, start_time: startTime, end_time: endTime });
        updateBreaks(false);
        // Mark these slots as cleared so derivedBreaks doesn't re-show them as "#".
        setClearedBreakSlots(prev => {
          const next = new Map(prev);
          const slots = new Set(prev.get(officerId) ?? []);
          for (let s = startSlot; s < endSlotExcl; s++) slots.add(s);
          next.set(officerId, slots);
          return next;
        });
        setAssignError(null); setDragSelection(null);
      } else if (action.kind === 'counter' && action.duty) {
        const allocation: ManualAllocation = {
          officer: officerId, type: meta?.officer_type ?? "MAIN",
          counter: 0, duty: action.duty, start: startTime, end: endTime,
          duration: calcDurationMins(startTime, endTime, shiftType),
          deployment: meta?.deployment, name: meta?.name,
        };
        result = await apiClient.manualAllocate({ allocations: [allocation] });
        updateBreaks(false);
        setAssignError(null); setDragSelection(null);
      } else if (action.kind === 'break') {
        // Only call deleteOfficer if the range actually has a counter or a duty (green cells have neither).
        const schedule = officer_schedules[officerId] ?? [];
        const hasCounter = schedule.slice(startSlot, endSlotExcl).some(v => v > 0)
          || Array.from({ length: endSlotExcl - startSlot }, (_, k) => startSlot + k).some((s) => dutySlots?.[officerId]?.[String(s)]);
        if (hasCounter) {
          rollback = onOptimisticClear?.([{ officerKey: officerId, startSlot, endSlot: endSlotExcl }]);
          result = await apiClient.deleteOfficer({ officer: officerId, start_time: startTime, end_time: endTime });
        }
        updateBreaks(true);
        // Un-suppress: if user had previously cleared this slot, they now explicitly want "#".
        setClearedBreakSlots(prev => {
          const next = new Map(prev);
          const slots = new Set(prev.get(officerId) ?? []);
          for (let s = startSlot; s < endSlotExcl; s++) slots.delete(s);
          if (slots.size > 0) next.set(officerId, slots); else next.delete(officerId);
          return next;
        });
        setAssignError(null); setDragSelection(null);
      } else if (action.kind === 'counter') {
        const allocation: ManualAllocation = {
          officer: officerId, type: meta?.officer_type ?? "MAIN",
          counter: action.counter, start: startTime, end: endTime,
          duration: calcDurationMins(startTime, endTime, shiftType),
          deployment: meta?.deployment, name: meta?.name,
        };
        result = await apiClient.manualAllocate({ allocations: [allocation] });
        updateBreaks(false);
        setAssignError(null); setDragSelection(null);
      }
      onUpdated?.(result);
    } catch (err) {
      rollback?.();
      setAssignError(err instanceof Error ? err.message : "Failed to assign");
    }
  };

  const handleCancelAssign = () => {
    setAssignError(null); setDragSelection(null);
    if (rendererRef.current && fullGridDataRef.current) rendererRef.current.render(fullGridDataRef.current);
  };

  const handleRangeChange = (startTime: string, endTime: string) => {
    setAssignError(null);
    setDragSelection((sel) => (sel ? { ...sel, startTime, endTime } : sel));
  };

  // The highlight is what the user checks the range against, so it has to follow
  // the popover's time pickers rather than staying where the finger left it.
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer || !dragSelection) return;
    const sh = getStartHour(shiftType);
    const first = hmmmToSlot(dragSelection.startTime, sh);
    const last = hmmmToEndSlot(dragSelection.endTime, sh) - 1;
    if (last >= first) renderer.paintDragHighlight(dragSelection.row, first, last, dragSelection.mode);
  }, [dragSelection, shiftType]);

  // Where the merged cells sit on the canvas, for the phone icons pinned to them.
  const [segmentRects, setSegmentRects] = useState<ReturnType<RosterCanvas['getSegmentRects']>>([]);
  const segmentRectsKeyRef = useRef('');
  // A block still owed a call reads red, for exactly as long as its phone icon is up.
  useEffect(() => {
    rendererRef.current?.setPendingCalls((callBlocks ?? [])
      .filter((b) => !callAcked?.has(ackKey(b.officerId, b.signature)))
      .map((b) => ({ officerId: b.officerId, row: null, start: b.startSlot, end: b.endSlot })));
  }, [callBlocks, callAcked, segmentRects, rendererVersion]);

  // Keep each block's start time/label clear of its phone icon.
  useEffect(() => {
    const insets = new Map<string, number>();
    for (const { block, rect, size } of callIcons(callBlocks ?? [], segmentRects, 'officer')) {
      insets.set(`${block.officerId}|${rect.row}|${rect.startCol}`, size + 2);
    }
    rendererRef.current?.setTextInsets(insets);
  }, [callBlocks, segmentRects, rendererVersion]);

  // ...and clear of its NR button.
  const { marks: noReliefMarks } = useNoReliefView();
  useEffect(() => {
    rendererRef.current?.setRightInsets(nrRightInsets(noReliefMarks, segmentRects, 'officer'));
  }, [noReliefMarks, segmentRects, rendererVersion]);

  // ── Canvas init ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current) return;
    const renderer = new RosterCanvas(
      canvasRef.current,
      { cellWidth: 50, cellHeight: dm.minH, labelWidth: 0, labelHeight: 0, fontSize: dm.headerFont, mergeConsecutive: true, textMode: "cell_text", density, noteLane: true },
      (info) => setHoverInfo(info),
      stableCanvasClick,
      stableDragSelect,
    );
    rendererRef.current = renderer;
    renderer.setAfterRender(() => {
      const rects = renderer.getSegmentRects();
      const key = JSON.stringify(rects);
      if (key === segmentRectsKeyRef.current) return;
      segmentRectsKeyRef.current = key;
      setSegmentRects(rects);
    });
    setRendererVersion((v) => v + 1);
    return () => { renderer.destroy(); rendererRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A new preset: hand the renderer its column floor and base row height before the layout
  // effects below re-measure (effects run in order, so this must stay above them).
  useEffect(() => {
    rendererRef.current?.setDensity(density);
    rendererRef.current?.setCellHeight(dm.minH);
  }, [density, dm]);

  useEffect(() => {
    rendererRef.current?.setLiveNowSlot(liveNow?.slot ?? null, liveNow?.pinned ?? false);
  }, [liveNow?.slot, liveNow?.pinned]);

  useEffect(() => {
    if (!rendererRef.current) return;
    rendererRef.current.setHighlightedOfficers(highlightOfficerIds ?? null);
  }, [highlightOfficerIds]);

  // The scroll container's content width as the ResizeObserver last reported it — fractional
  // (e.g. 505.813), where clientWidth is rounded to a whole pixel. The effect below and the
  // observer must lay the grid out on the SAME width: when the effect measured clientWidth
  // itself, any roster update after the observer had settled snapped the grid to the rounded
  // width, and the unchanged container size never woke the observer to correct it. The
  // clientWidth fallback only covers the moment before the observer's first report, which
  // always follows and applies the real width.
  const observedWidthRef = useRef<number | null>(null);
  useEffect(() => {
    if (!rendererRef.current || !gridData) return;
    rendererRef.current.setNoteLane(noteLane ?? { tracks: new Map(), lineH: 0 });
    rendererRef.current.setRowHeights(officerRowHeights);
    // Resizing draws the grid, so the new data goes in first and it is drawn once.
    rendererRef.current.setData(gridData);
    // The observer's fractional width, not the rounded clientWidth — see observedWidthRef.
    const width = mainScrollRef.current ? observedWidthRef.current ?? mainScrollRef.current.clientWidth : 0;
    if (width - dm.labelW > 0) rendererRef.current.resizeWidth(width - dm.labelW);
    else rendererRef.current.render(gridData);
    const cfg = rendererRef.current?.getConfig();
    if (cfg) setLayoutInfo((prev) =>
      prev.labelWidth === cfg.labelWidth && prev.labelHeight === cfg.labelHeight
        && prev.cellWidth === cfg.cellWidth && prev.cellHeight === cfg.cellHeight
        ? prev
        : { labelWidth: cfg.labelWidth, labelHeight: cfg.labelHeight, cellWidth: cfg.cellWidth, cellHeight: cfg.cellHeight });
  }, [gridData, officerRowHeights, noteLane, mainScrollRef, dm]);

  // Skips a fire that reports the same rounded width as last time — e.g. a nearby
  // CSS transition (the officer-edit side panel's margin animation) or a
  // split-divider drag can wake this observer repeatedly without this container's
  // own width actually changing. See the matching guard in RosterGrid.tsx.
  const lastWidthRef = useRef<number | null>(null);
  useEffect(() => {
    if (!mainScrollRef.current) return;
    lastWidthRef.current = null;   // a new preset re-measures even at an unchanged width
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        observedWidthRef.current = entry.contentRect.width;
        // The container holds the label column too; the canvas gets the rest.
        const width = entry.contentRect.width - dm.labelW;
        if (width <= 0) continue;
        if (lastWidthRef.current !== null && Math.round(lastWidthRef.current) === Math.round(width)) continue;
        lastWidthRef.current = width;
        rendererRef.current?.resizeWidth(width);
        const cfg = rendererRef.current?.getConfig();
        if (cfg) setLayoutInfo({ labelWidth: cfg.labelWidth, labelHeight: cfg.labelHeight, cellWidth: cfg.cellWidth, cellHeight: cfg.cellHeight });
        if (rendererRef.current && fullGridDataRef.current) {
          rendererRef.current.render(fullGridDataRef.current);
        }
      }
    });
    observer.observe(mainScrollRef.current);
    return () => observer.disconnect();
  }, [mainScrollRef, dm]);

  // A wide layout opens on the present, not on 10:00 with "now" off-screen to the right.
  useEffect(() => {
    const scroller = mainScrollRef.current;
    if (!scroller || DENSITY_PRESETS[density].minCellWidth === 0 || scroller.scrollLeft !== 0) return;
    const frame = requestAnimationFrame(() => {
      const cfg = rendererRef.current?.getConfig();
      if (cfg) scroller.scrollLeft = Math.max(0, (liveNow?.slot ?? 0) * cfg.cellWidth - scroller.clientWidth * 0.3);
    });
    return () => cancelAnimationFrame(frame);
  }, [density]); // eslint-disable-line react-hooks/exhaustive-deps

  const cursor = hoverInfo?.officer ? "pointer" : "crosshair";
  const startHour = getStartHour(shiftType);
  return (
    <div style={{
      width: "100%",
      // growToFitRows: the grid panel below is what is sized to the rows; this box just wraps it
      // and the legend, so the legend takes its own height instead of being carved out of the
      // panel's (which clipped the last row, with overflowY locked so nothing could scroll it back).
      height: growToFitRows ? undefined : (height ?? "calc(100dvh - 185px)"),
      minHeight: growToFitRows ? '0' : (height === '100%' ? '0' : '200px'),
      display: "flex", flexDirection: "column",
      // Long-pressing anywhere in this grid (to split/add an availability window,
      // or drag-select cells) must never trigger the browser's native
      // text-selection/callout — that's what turns into a page-wide blue
      // highlight on iOS Safari.
      userSelect: "none", WebkitUserSelect: "none",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      WebkitTouchCallout: "none" as any,
    }}>
      {/* ── 3-panel grid ── */}
      <div
        ref={gridPanelRef}
        style={{
          // + 2 for the panel's own 1px top and bottom border.
          ...(growToFitRows
            ? { flex: "0 0 auto", boxSizing: "border-box" as const, height: dm.headerH + totalContentHeight + 2 }
            : { flex: 1, minHeight: 0 }),
          border: "1px solid #000", position: "relative",
          overflowX: "auto",
          // growToFitRows sizes this box to exactly fit every row — locked so a vertical swipe
          // here always falls through to the page instead of the two fighting over it.
          overflowY: growToFitRows ? "hidden" : "auto",
          WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"],
        }}
      >
       <div style={{ display: "grid", gridTemplateRows: `${dm.headerH}px auto`, gridTemplateColumns: `${dm.labelW}px max-content${onEndNoteChange ? ` ${END_NOTE_W}px` : ''}`, width: "max-content", minWidth: "100%" }}>
        {/* Corner — filter button */}
        <div style={{ position: "sticky", top: 0, left: 0, zIndex: 4, background: "#f8fafc", borderRight: "1px solid #000", borderBottom: "1px solid #000", overflow: 'visible' }}>
          <div style={{ display: 'flex', width: '100%', height: '100%', gap: 3, padding: 3, boxSizing: 'border-box' }}>
            <button
              ref={cornerBtnRef}
              onClick={handleFilterToggle}
              title={`${(hiddenOfficerIds?.size ?? 0) > 0 ? `${hiddenOfficerIds!.size} rows hidden — click to change` : 'Filter officer rows'}`}
              style={{
                flex: 1, minWidth: 0, height: '100%', background: filterOpen || (hiddenOfficerIds?.size ?? 0) > 0 ? '#dbeafe' : '#e0e7ff',
                border: `1px solid ${filterOpen || (hiddenOfficerIds?.size ?? 0) > 0 ? '#60a5fa' : '#a5b4fc'}`, borderRadius: 6, boxShadow: '0 1px 2px rgba(55,48,163,0.18)', cursor: 'pointer', padding: '0 4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
                color: (hiddenOfficerIds?.size ?? 0) > 0 ? '#1d4ed8' : '#3730a3',
              }}
            >
              {(hiddenOfficerIds?.size ?? 0) > 0 ? (
                /* Eye-off icon — rows are hidden */
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                /* Filter funnel icon — nothing hidden */
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              )}
              <span style={{ fontSize: sc(10), fontWeight: 800 }}>
                {(hiddenOfficerIds?.size ?? 0) > 0 ? `${hiddenOfficerIds!.size} hidden` : 'Filter'}
              </span>
            </button>
            <button
              onClick={() => (editMode ? exitEditMode() : setEditMode(true))}
              title={editMode ? 'Exit edit mode (Esc)' : 'Edit — reorder, hide or delete rows (swipe a row left to delete, right to hide). Select several: Ctrl/Cmd+click a row, or press and hold it on a phone'}
              style={{
                flex: 1, minWidth: 0, height: '100%', background: editMode ? '#fee2e2' : '#e0e7ff',
                border: `1px solid ${editMode ? '#f87171' : '#a5b4fc'}`, borderRadius: 6, boxShadow: '0 1px 2px rgba(55,48,163,0.18)', cursor: 'pointer', padding: '0 4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
                color: editMode ? '#dc2626' : '#3730a3',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
              <span style={{ fontSize: sc(10), fontWeight: 800 }}>{editMode ? 'Done' : 'Edit'}</span>
            </button>
            <RulerToggle size={dm.headerH - 8} />
          </div>
        </div>

        {/* Time header */}
        <div style={{ position: "sticky", top: 0, zIndex: 3, background: "#f8fafc", borderBottom: "1px solid #000", height: dm.headerH }}>
          <div style={{ width: layoutInfo.cellWidth * 48, height: dm.headerH, position: "relative" }}>
            <RulerHeaderMarker ruler={ruler} cellWidth={layoutInfo.cellWidth} mode={operation_mode} />
            {time_labels.map((label, index) => {
              if (index % 4 !== 0) return null;
              return (
                <div key={index} style={{ position: "absolute", left: index * layoutInfo.cellWidth, width: layoutInfo.cellWidth * 4, height: dm.headerH, display: "flex", alignItems: "center", justifyContent: "center", fontSize: dm.headerFont, color: "#000", fontFamily: "system-ui, -apple-system, sans-serif" }}>
                  {label}
                </div>
              );
            })}
            {liveNow && (
              <LiveNowFlag x={liveNow.slot * layoutInfo.cellWidth} label={liveNow.label}
                totalWidth={layoutInfo.cellWidth * 48} height={dm.headerH}
                cellWidth={layoutInfo.cellWidth} pinned={liveNow.pinned} liveLabel={liveNow.liveLabel} />
            )}
          </div>
        </div>

        {onEndNoteChange && (
          <div style={{ position: "sticky", top: 0, zIndex: 3, background: "#fffbeb", borderBottom: "1px solid #000", borderLeft: "1px solid #000", height: dm.headerH, display: "flex", alignItems: "center", justifyContent: "center", fontSize: dm.headerFont, fontWeight: 700, color: "#92400e", whiteSpace: "nowrap" }}
            title="HOTO: where an OT officer is on break till the end of the shift, say when they report to the next shift's car field">
            HOTO note
          </div>
        )}

        {/* Officer labels column */}
        <div style={{ position: "sticky", left: 0, zIndex: 2, background: "#fff", borderRight: "1px solid #000", width: dm.labelW }}>
         <div ref={labelListRef} style={{ position: "relative" }}>
          {rowDrag && rowDragLineTop !== null && (
            <>
              <div style={{ position: 'absolute', left: 0, right: 0, top: rowDragSrcTop, height: officerRowHeights[rowDragSrcIdx] ?? 0, background: 'rgba(59,130,246,0.14)', pointerEvents: 'none', zIndex: 5 }} />
              <div style={{ position: 'absolute', left: 0, right: 0, top: rowDragLineTop - 2, height: 4, borderRadius: 2, background: rowDragNoop ? '#94a3b8' : '#2563eb', pointerEvents: 'none', zIndex: 6 }} />
            </>
          )}
          {sortedOfficerIds.map((officerId, rowIdx) => {
            const meta = officer_metadata[officerId];
            const schedule = officer_schedules[officerId] ?? [];
            const firstWorking = schedule.findIndex((v) => v > 0);
            const lastWorking = schedule.length - 1 - [...schedule].reverse().findIndex((v) => v > 0);
            const hasWork = firstWorking >= 0;

            // Fall back to key prefix when metadata is absent (e.g. officer from old history snapshot).
            const isSOSOT = meta
              ? (meta.officer_type === 'SOS' || meta.officer_type === 'OT')
              : (officerId.startsWith('OT') || (officerId.startsWith('S') && !officerId.startsWith('SN')));

            // Use stored availability window for all officer types so the label
            // does not change when counter assignments are deleted.
            const availStart = meta?.availability_start || null;
            const availEnd   = meta?.availability_end   || null;

            const assignStart = hasWork ? slotToHmmm(firstWorking, startHour, 15) : null;
            const assignEnd   = hasWork ? slotToHmmm(lastWorking + 1, startHour, 15) : null;

            const bracketColor = meta?.officer_type === 'SOS' ? '#e879a0' : '#f59e0b';
            let timeDisplay: string | null = null;
            if (meta?.availability_segments && meta.availability_segments.length > 1) {
              timeDisplay = meta.availability_segments.map(s => `${fmtTime(s.start)}–${fmtTime(s.end)}`).join(' · ');
            } else if (availStart && availEnd) {
              timeDisplay = `${fmtTime(availStart)}–${fmtTime(availEnd)}`;
            } else if (assignStart && assignEnd) {
              timeDisplay = `${fmtTime(assignStart)}–${fmtTime(assignEnd)}`;
            }

            // For main officers: only show time when they're on a partial shift (not full shift).
            // Full-shift officers don't need a label; partial-shift ones get red to flag attention.
            let mainPartialTime: string | null = null;
            const mainExcl = !isSOSOT ? mainExclusionText(meta, startHour, availStart, availEnd, assignStart, assignEnd) : null;
            if (mainExcl) {
              mainPartialTime = mainExcl.windows || null;
            } else if (!isSOSOT && timeDisplay) {
              const bounds = SHIFT_BOUNDS[shiftType] ?? SHIFT_BOUNDS.morning;
              const effStart = availStart || assignStart;
              const effEnd   = availEnd   || assignEnd;
              const isFullShift = effStart === bounds.start && effEnd === bounds.end;
              if (!isFullShift) mainPartialTime = timeDisplay;
            }

            const nameDeployLine = officerLabel(officerId, meta);

            const rowHeight = officerRowHeights[rowIdx] ?? layoutInfo.cellHeight;
            const call = callStates?.get(officerId);
            const callActive = !!call && call.pending.length > 0;
            const selected = selectedIds.has(officerId);
            return (
              <div
                key={officerId}
                className="counter-label-overlay"
                title={nameDeployLine}
                style={{ position: "relative", width: dm.labelW, height: rowHeight, boxSizing: "border-box", flexShrink: 0, overflow: "hidden", background: ruler?.row === rowIdx ? rulerColor(operation_mode) : undefined }}
              >
                {/* Under the row, uncovered by a swipe: indigo "Hide" from the left, red "Delete" from the right.
                    Solid and "Release" once pulled far enough. */}
                <div data-swipe-strip="" aria-hidden="true" style={{ position: 'absolute', inset: 0, color: '#fff', fontSize: 11, fontWeight: 800 }}>
                  <div data-swipe-side="hide" style={{ position: 'absolute', inset: 0, display: 'none', alignItems: 'center', justifyContent: 'flex-start', gap: 4, paddingLeft: 8, background: '#a5b4fc' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    <span data-swipe-label="">Hide</span>
                  </div>
                  <div data-swipe-side="delete" style={{ position: 'absolute', inset: 0, display: 'none', alignItems: 'center', justifyContent: 'flex-end', gap: 4, paddingRight: 8, background: '#fca5a5' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    <span data-swipe-label="">Delete</span>
                  </div>
                </div>
                {/* The row itself — slides left in Edit mode to uncover the delete strip */}
                <div
                  data-swipe-row={officerId}
                  className={(editMode || swipeHint) && rowIdx === 0 ? 'officer-row-peek' : undefined}
                  onPointerDown={(e) => startSwipe(e, officerId)}
                  onPointerMove={moveSwipe}
                  onPointerUp={endSwipe}
                  onPointerCancel={endSwipe}
                  style={{
                    position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', height: '100%',
                    background: selected ? '#fee2e2' : ruler?.row === rowIdx ? rulerColor(operation_mode) : '#fff',
                    transform: 'translateX(0px)',
                    transition: 'transform 0.18s ease-out',
                    touchAction: 'pan-y',
                    boxShadow: selected ? 'inset 3px 0 0 #dc2626' : undefined,
                  }}
                >
                  {/* Call badge: normal mode only, a full-height column always in the same place. It stays
                      (as an outline with a tick) once the officer has been told, so it can be re-armed. */}
                  {dm.callW > 0 && (
                    <div style={{ width: dm.callW, alignSelf: 'stretch', flexShrink: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'center', borderRight: '1px solid #e2e8f0', background: callActive ? '#eef2ff' : undefined }}>
                      {call && call.total > 0 && (
                        <CallPhoneButton
                          active={callActive}
                          size={sc(17)}
                          hitWidth={dm.callW - 1}
                          hitHeight={rowHeight}
                          disabled={!canEdit}
                          title={!canEdit
                            ? 'View only — switch to Edit to tick'
                            : callActive
                              ? `Call ${nameDeployLine} about ${call.pending.length} change${call.pending.length === 1 ? '' : 's'}, then click to mark informed`
                              : 'Informed ✓ — click to undo'}
                          onClick={() => onToggleOfficerCall?.(officerId, callActive)}
                        />
                      )}
                    </div>
                  )}
                  {/* Edit mode: drag handle, left — press and drag to move this row anywhere */}
                  {editMode && (
                    <div
                      onPointerDown={(e) => { e.stopPropagation(); startRowDrag(e, officerId); }}
                      title="Drag to reorder"
                      aria-label={`Drag to reorder ${officerId}`}
                      role="button"
                      style={{
                        alignSelf: 'stretch', flexShrink: 0, width: dm.gripW, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: rowDrag?.id === officerId ? 'grabbing' : 'grab', color: rowDrag?.id === officerId ? '#2563eb' : '#94a3b8',
                        touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        WebkitTouchCallout: 'none' as any, WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <svg width="8" height="14" viewBox="0 0 6 10" fill="currentColor" aria-hidden="true">
                        {[1, 5, 9].flatMap(y => [1, 5].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="0.9" />))}
                      </svg>
                    </div>
                  )}
                  {/* Text content — opens OfficerEditPopup on click (Edit mode: toggles selection) */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      if (suppressNextClickRef.current) { suppressNextClickRef.current = false; return; }
                      if (editMode || e.ctrlKey || e.metaKey || e.shiftKey) { selectRow(officerId, e); return; }
                      onOfficerClick?.({ officer: officerId, counter: 'label', time: 'label', metadata: meta } as HoverInfo);
                    }}
                    onPointerDown={(e) => startLongPress(e, officerId)}
                    onPointerMove={moveLongPress}
                    onPointerUp={cancelLongPress}
                    onPointerCancel={cancelLongPress}
                    onContextMenu={(e) => { if (suppressNextClickRef.current) e.preventDefault(); }}
                    style={{ flex: 1, minHeight: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden', padding: '2px 3px 2px 4px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent', scrollbarWidth: 'thin', WebkitTouchCallout: 'none' as never, WebkitUserSelect: 'none', userSelect: 'none' }}
                  >
                  {/* Name, time and duty lines wrap freely; when they outgrow the row the whole
                        block scrolls inside this label rather than being clipped. */}
                  <span style={{ fontWeight: 700, fontSize: dm.namePx, lineHeight: LABEL_LINE_H, color: '#1e293b', whiteSpace: 'normal', wordBreak: 'break-word', flexShrink: 0 }}>
                    {nameDeployLine}
                  </span>
                  {(() => {
                    const timeText = isSOSOT ? timeDisplay : mainPartialTime;
                    const timeColor = isSOSOT ? bracketColor : '#dc2626';
                    if (!timeText && !mainExcl) return null;
                    return (
                      <>
                        {timeText && (
                          <span style={{ fontSize: dm.timePx, lineHeight: LABEL_LINE_H, color: timeColor, fontWeight: 600, display: 'block', whiteSpace: 'normal', wordBreak: 'break-all', flexShrink: 0 }}>
                            {timeText}
                          </span>
                        )}
                        {mainExcl && (
                          <span title="Away on official duty — not a break" style={{ fontSize: dm.timePx, lineHeight: LABEL_LINE_H, color: '#b45309', fontWeight: 600, display: 'block', whiteSpace: 'normal', wordBreak: 'break-all', flexShrink: 0 }}>
                            {mainExcl.duty}
                          </span>
                        )}
                      </>
                    );
                  })()}
                  </div>
                  {/* Edit mode: hide this row (eye), right — it comes back through the Filter button */}
                  {editMode && onChangeHiddenIds && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const next = new Set(hiddenOfficerIds);
                        if (next.has(officerId)) next.delete(officerId); else next.add(officerId);
                        onChangeHiddenIds(next);
                      }}
                      title="Hide this row (bring it back from Filter)"
                      aria-label={`Hide ${nameDeployLine}`}
                      style={{ alignSelf: 'stretch', flexShrink: 0, width: dm.eyeW, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', borderLeft: '1px solid #e2e8f0', padding: 0, color: '#475569', cursor: 'pointer', WebkitTapHighlightColor: 'rgba(102,126,234,0.25)' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                  )}
                  {/* Edit mode: a slim tint on the right edge — "this row slides left" (the whole row is the handle) */}
                  {editMode && (
                    <div
                      aria-hidden="true"
                      style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: 4, height: Math.min(22, rowHeight - 8), borderRadius: '3px 0 0 3px', background: '#fca5a5', pointerEvents: 'none' }}
                    />
                  )}
                </div>
              </div>
            );
          })}
         </div>
        </div>

        {/* Main scroll area */}
        <div>
          <div
            style={{ position: "relative", width: layoutInfo.cellWidth * num_slots, height: totalContentHeight }}
            // Observe-only: light up the pressed cell's row and column (see ReadingRuler).
            {...tapHandlers((clientX, clientY) => {
              const canvas = canvasRef.current;
              if (!canvas) return null;
              const rect = canvas.getBoundingClientRect();
              const x = clientX - rect.left;
              const y = clientY - rect.top;
              const col = Math.floor(x / layoutInfo.cellWidth);
              let top = 0;
              let row = -1;
              for (let r = 0; r < officerRowHeights.length; r++) {
                if (y >= top && y < top + officerRowHeights[r]) { row = r; break; }
                top += officerRowHeights[r];
              }
              return row >= 0 && col >= 0 && col < num_slots ? { row, col } : null;
            })}
          >
            {ruler && ruler.row < officerRowHeights.length && (
              <ReadingRulerBands
                ruler={ruler}
                rowTop={officerRowHeights.slice(0, ruler.row).reduce((a, b) => a + b, 0)}
                rowHeight={officerRowHeights[ruler.row]}
                cellWidth={layoutInfo.cellWidth}
                totalWidth={layoutInfo.cellWidth * num_slots}
                totalHeight={totalContentHeight}
              />
            )}
            {rowDrag && rowDragLineTop !== null && (
              <>
                <div style={{ position: 'absolute', left: 0, right: 0, top: rowDragSrcTop, height: officerRowHeights[rowDragSrcIdx] ?? 0, background: 'rgba(59,130,246,0.10)', pointerEvents: 'none', zIndex: 5 }} />
                <div style={{ position: 'absolute', left: 0, right: 0, top: rowDragLineTop - 2, height: 4, borderRadius: 2, background: rowDragNoop ? '#94a3b8' : '#2563eb', pointerEvents: 'none', zIndex: 6 }} />
              </>
            )}
            <canvas
              ref={canvasRef}
              onMouseMove={(e) => {
                mousePosRef.current = { x: e.clientX, y: e.clientY };
                const el = tooltipRef.current;
                if (el) { el.style.top = `${e.clientY + 16}px`; el.style.left = `${e.clientX + 16}px`; }
              }}
              onMouseLeave={() => { mousePosRef.current = null; setHoverInfo(null); }}
              // Without these, a touch long-press (e.g. to split/add an availability
              // window) triggers the browser's native text-selection/callout gesture
              // instead — the "whole page goes blue" highlight on iOS Safari, or the
              // selection-handle magnifier on Android.
              style={{
                cursor, display: "block",
                userSelect: "none", WebkitUserSelect: "none",
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                WebkitTouchCallout: "none" as any,
              }}
            />
            <NoReliefCellOverlay rects={segmentRects} view="officer" operationMode={operation_mode} />
            {callBlocks && callBlocks.length > 0 && (
              <CallPhoneOverlay
                view="officer"
                blocks={callBlocks}
                acked={callAcked}
                rects={segmentRects}
                canEdit={canEdit}
                onToggle={(block, called) => onToggleCall?.(block, called)}
              />
            )}
          </div>
        </div>

        {onEndNoteChange && (
          <div style={{ borderLeft: "1px solid #000", background: "#fff" }}>
            {sortedOfficerIds.map((officerId, rowIdx) => {
              const meta = officer_metadata[officerId];
              const isOT = meta ? meta.officer_type === 'OT' : officerId.startsWith('OT');
              return (
                <div key={officerId} style={{ height: officerRowHeights[rowIdx] ?? layoutInfo.cellHeight, boxSizing: "border-box", padding: isOT ? 2 : 0, borderBottom: "1px solid #e5e7eb" }}>
                  {isOT && (
                    <input
                      type="text"
                      value={endNotes?.[officerId] ?? ''}
                      onChange={(e) => onEndNoteChange(officerId, e.target.value)}
                      placeholder="break till … / reports …"
                      aria-label={`HOTO note for ${officerLabel(officerId, meta)}`}
                      style={{ width: "100%", height: "100%", boxSizing: "border-box", padding: "0 6px", border: endNotes?.[officerId] ? "1px solid #f59e0b" : "1px solid #e2e8f0", borderRadius: 4, fontSize: 12, background: endNotes?.[officerId] ? "#fffbeb" : "#fff", minWidth: 0 }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
       </div>
      </div>

      {rowDrag && (
        <div style={{
          position: 'fixed', left: rowDrag.x + 14, top: rowDrag.y - 18, zIndex: 9500, pointerEvents: 'none',
          background: '#1e293b', color: 'white', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 700,
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)', maxWidth: 'min(260px, 70vw)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {officerLabel(rowDrag.id, officer_metadata[rowDrag.id])}
        </div>
      )}

      {/* Press-and-hold in availability-view mode: split an existing window, add a new
          one, or — pressing right on a boundary two windows already share — merge them
          back into one (also recombines any counter run the officer card had been
          showing split at that same boundary, since that split was cosmetic — a display
          artifact of the boundary, not a real change to the underlying assignment). */}
      {pressPopover && createPortal(
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 1500 }} onClick={() => setPressPopover(null)} />
          <button
            onClick={() => {
              const { officerId, col, kind, mergeSegIndex } = pressPopover;
              setPressPopover(null);
              if (kind === 'split') void splitWindowAt(officerId, col);
              else if (kind === 'merge') { if (mergeSegIndex !== undefined) void mergeWindowsAt(officerId, mergeSegIndex); }
              else void addWindowAt(officerId, col);
            }}
            style={{
              position: "fixed", left: pressPopover.x, top: pressPopover.y, transform: "translate(-50%, -50%)",
              zIndex: 1501, background: "#1e293b", color: "white", border: "none", borderRadius: 20,
              padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
              whiteSpace: "nowrap",
            }}
          >
            {pressPopover.kind === 'split' ? '⟩⟨ Split window' : pressPopover.kind === 'merge' ? '⟨ Merge windows ⟩' : '⟨⟩ Add window'}
          </button>
        </>,
        document.body,
      )}

      {editMode && createPortal(
        <div style={{
          position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 1600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 8, padding: '8px 12px', background: '#1e293b', color: 'white',
          borderRadius: 24, whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,0.35)', fontSize: 13, fontWeight: 600, maxWidth: 'calc(100vw - 24px)',
        }}>
          <span style={{ whiteSpace: 'nowrap' }}>{selectedIds.size === 0 ? 'Swipe left to delete · right to hide' : `${selectedIds.size} selected`}</span>
          <button
            onClick={() => setSelectedIds(selectedIds.size === sortedOfficerIds.length ? new Set() : new Set(sortedOfficerIds))}
            style={{ background: 'transparent', border: '1px solid #64748b', color: 'white', borderRadius: 14, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
          >{selectedIds.size === sortedOfficerIds.length ? 'Clear' : 'Select all'}</button>
          <button
            disabled={selectedIds.size === 0}
            onClick={() => setPendingDelete(sortedOfficerIds.filter(id => selectedIds.has(id)))}
            style={{ background: '#dc2626', border: 'none', color: 'white', borderRadius: 14, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer', opacity: selectedIds.size === 0 ? 0.5 : 1 }}
          >Delete</button>
          <button onClick={exitEditMode} style={{ background: 'transparent', border: 'none', color: '#cbd5e1', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
        </div>,
        document.body,
      )}

      {pendingDelete && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setPendingDelete(null)}>
          <div style={{ background: 'white', borderRadius: 12, padding: '22px 26px', maxWidth: 380, width: '100%', zoom: dialogScale, boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#1e293b' }}>
              Delete {pendingDelete.length === 1 ? 'officer' : `${pendingDelete.length} officers`}?
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
              This removes {pendingDelete.length === 1 ? 'the officer' : 'these officers'} and all their counter assignments from the roster.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18, maxHeight: 160, overflowY: 'auto' }}>
              {pendingDelete.map(id => (
                <span key={id} style={{ fontSize: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '3px 8px', color: '#991b1b', fontWeight: 600 }}>
                  {officerLabel(id, officer_metadata[id])}
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button autoFocus onClick={() => void confirmDelete()} style={{ flex: 1, padding: 9, background: '#dc2626', color: 'white', border: 'none', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Delete</button>
              <button onClick={() => setPendingDelete(null)} style={{ flex: 1, padding: 9, background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Filter dropdown — rendered with position:fixed to escape overflow:hidden on the grid */}
      {filterOpen && filterAnchor && onChangeHiddenIds && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 400 }} onClick={() => { setFilterOpen(false); setFilterSearchText(''); }} />
          <div style={{
            position: 'fixed', top: filterAnchor.top, left: filterAnchor.left,
            width: 300, maxHeight: '65vh', overflowY: 'auto',
            background: 'white', border: '1px solid #e2e8f0',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 401, padding: 8,
            transform: `translate(${filterDrag.offset.dx}px, ${filterDrag.offset.dy}px)`,
          }}>
            <div style={{ margin: '-8px -8px 8px' }}><DragBar drag={filterDrag} /></div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, paddingBottom: 6, borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#475569' }}>Show / Hide Officers</span>
              {customOrder.length > 0 && (
                <button
                  onClick={() => saveCustomOrder([])}
                  title="Put the rows back in their default order"
                  style={{ fontSize: 13, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}
                >
                  Reset order
                </button>
              )}
              {(hiddenOfficerIds?.size ?? 0) > 0 && (
                <button
                  onClick={() => { onChangeHiddenIds(new Set()); setFilterOpen(false); setFilterSearchText(''); }}
                  style={{ fontSize: 13, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}
                >
                  Show All
                </button>
              )}
            </div>
            {/* Excel-style search box — narrows the list below to matching officers */}
            <input
              type="text"
              autoFocus
              value={filterSearchText}
              onChange={(e) => setFilterSearchText(e.target.value)}
              placeholder="Search…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '6px 9px', fontSize: 14, border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', marginBottom: 6, color: '#1e293b' }}
            />
            {/* Groups */}
            {officerGroups.map(group => {
              const hiddenInGroup = group.ids.filter(id => hiddenOfficerIds?.has(id)).length;
              const allVisible = hiddenInGroup === 0;
              const someHidden = hiddenInGroup > 0 && hiddenInGroup < group.ids.length;
              const q = filterSearchText.trim().toLowerCase();
              const visibleBatches = group.batches
                .map(batch => ({ ...batch, ids: batch.ids.filter(id => officerMatchesFilterSearch(id, q)) }))
                .filter(batch => batch.ids.length > 0);
              if (q && visibleBatches.length === 0) return null;
              return (
                <div key={group.type} style={{ marginBottom: 6 }}>
                  {/* Type row */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={allVisible}
                      ref={el => { if (el) el.indeterminate = someHidden; }}
                      onChange={() => {
                        const next = new Set(hiddenOfficerIds);
                        if (allVisible) group.ids.forEach(id => next.add(id));
                        else group.ids.forEach(id => next.delete(id));
                        onChangeHiddenIds(next);
                      }}
                      style={{ width: 15, height: 15, accentColor: '#667eea', cursor: 'pointer' }}
                    />
                    {group.label}
                    <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 13 }}>
                      ({group.ids.length - hiddenInGroup}/{group.ids.length})
                    </span>
                  </label>
                  {/* Batches */}
                  {visibleBatches.map(batch => {
                    const batchHidden = batch.ids.filter(id => hiddenOfficerIds?.has(id)).length;
                    const batchAllVisible = batchHidden === 0;
                    const batchSomeHidden = batchHidden > 0 && batchHidden < batch.ids.length;
                    return (
                      <div key={batch.label} style={{ marginLeft: 16 }}>
                        {group.batches.length > 1 && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 0', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#64748b' }}>
                            <input
                              type="checkbox"
                              checked={batchAllVisible}
                              ref={el => { if (el) el.indeterminate = batchSomeHidden; }}
                              onChange={() => {
                                const next = new Set(hiddenOfficerIds);
                                if (batchAllVisible) batch.ids.forEach(id => next.add(id));
                                else batch.ids.forEach(id => next.delete(id));
                                onChangeHiddenIds(next);
                              }}
                              style={{ width: 15, height: 15, accentColor: '#667eea', cursor: 'pointer' }}
                            />
                            {batch.label}
                          </label>
                        )}
                        {/* Individual officers */}
                        {batch.ids.map(id => {
                          const m = officer_metadata[id];
                          const nameLabel = [m?.deployment, m?.name].filter(Boolean).join(' - ');
                          const isHidden = hiddenOfficerIds?.has(id) ?? false;
                          return (
                            <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 0', cursor: 'pointer', fontSize: 14, color: '#374151', marginLeft: group.batches.length > 1 ? 14 : 0 }}>
                              <input
                                type="checkbox"
                                checked={!isHidden}
                                onChange={() => {
                                  const next = new Set(hiddenOfficerIds);
                                  if (isHidden) next.delete(id); else next.add(id);
                                  onChangeHiddenIds(next);
                                }}
                                style={{ width: 15, height: 15, accentColor: '#667eea', cursor: 'pointer', flexShrink: 0 }}
                              />
                              <span style={{ fontWeight: 600 }}>{id}</span>
                              {nameLabel && <span style={{ color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameLabel}</span>}
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {filterSearchText.trim() && !allOfficerIds.some(id => officerMatchesFilterSearch(id, filterSearchText.trim().toLowerCase())) && (
              <div style={{ fontSize: 13, color: '#94a3b8', padding: '6px 2px' }}>No officers match “{filterSearchText.trim()}”.</div>
            )}
          </div>
        </>
      )}

      {/* Color legend */}
      <div style={{ display: 'flex', gap: 10, padding: '6px 2px 2px', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
        {([
          { color: '#a2d2ff', label: 'Main' },
          { color: '#ffc6d9', label: 'SOS' },
          { color: DUTY_FILL, label: NON_COUNTER_LEGEND_LABEL },
          { color: '#FDFD96', label: 'OT' },
        ] as { color: string; label: string }[]).map(({ color, label }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: color, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 2, flexShrink: 0 }} />
            {label}
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, lineHeight: 1 }}>#</span>
          Break
        </span>
        <span title="A corner flag marks a cell with a saved note — tap the cell to read it" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
          <span style={{ width: 0, height: 0, borderTop: '9px solid #f59e0b', borderLeft: '9px solid transparent', flexShrink: 0 }} />
          Note
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
          <span style={{ display: 'inline-block', width: 12, height: 12, background: AVAIL_UNASSIGNED_COLOR, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 2, flexShrink: 0 }} />
          Available (unplanned)
        </span>
      </div>

      {/* Hover tooltip — same card as the Counter tab; here the block's own text is the counter. */}
      {hoverInfo?.officer && mousePosRef.current && !dragSelection && (
        <GridHoverCard
          ref={tooltipRef}
          info={hoverInfo}
          x={mousePosRef.current.x}
          y={mousePosRef.current.y}
          counterLabel={hoverInfo.blockText || hoverInfo.counter.split('\n')[0]}
        />
      )}

      {/* CounterAssignPopover */}
      {dragSelection && (
        <CounterAssignPopover startTime={dragSelection.startTime} endTime={dragSelection.endTime} shiftType={shiftType} onRangeChange={handleRangeChange} anchorX={dragSelection.x} anchorY={dragSelection.y} numCounters={numCounters} operation_mode={operation_mode} filled={dragSelection.mode === 'erase'} initialChoice={choiceAtSelection(dragSelection)} onAction={handleCellAction} onCancel={handleCancelAssign} errorMessage={assignError ?? undefined}
          vacancyOf={(n, start) => counterVacancyOnSchedules(
            officer_schedules, num_slots, n, hmmmToSlot(start, getStartHour(shiftType)), dragSelection.officerId,
            (key) => officer_metadata[key]?.name || key,
          )} />
      )}

    </div>
  );
}
